// ── Global Constants & Utilities ──────────────────────────────────
const PUB_SUB_EVENTS = {
  cartUpdate: "cart-update", // Cart updated
  quantityUpdate: "quantity-update", // Product quantity changed
  variantChange: "variant-change", // Product variant changed
  cartError: "cart-error", // Cart error
};
window.PUB_SUB_EVENTS = PUB_SUB_EVENTS;

const NO_DECIMAL_CURRENCIES = [
  "BIF",
  "CLP",
  "DJF",
  "GNF",
  "JPY",
  "KMF",
  "KRW",
  "MGA",
  "PYG",
  "RWF",
  "UGX",
  "VND",
  "VUV",
  "XAF",
  "XOF",
  "XPF",
];

// Global JavaScript functions and variables
const webvista = (function () {
  // DOM ready detection
  function onReady(callback) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", callback);
    } else {
      callback();
    }
  }

  /**
   * Fetch HTML content
   * @param url
   * @param signal
   * @returns {Promise<Document>}
   */
  async function fetchHtml(url, signal = null) {
    const fetchOptions = signal ? { signal } : {};
    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      throw new Error(
        `HTTP error! Status: ${response.status} ${response.statusText}`,
      );
    }

    const text = await response.text();
    return new DOMParser().parseFromString(text, "text/html");
  }

  // Lazy image loading animation
  function initLazyImageAnimation(root = document) {
    const scope =
      root instanceof Event ? root.target || document : root || document;
    const images = Array.from(
      scope.querySelectorAll("img.image-lazy-loading[data-srcset]"),
    );
    if (!images.length) return;

    const applySrcset = (img) => {
      const srcset = img.dataset.srcset;
      if (!srcset) return;
      img.srcset = srcset;
      img.removeAttribute("data-srcset");
      const onLoad = () => {
        img.classList.remove("image-lazy-placeholder");
        img.classList.remove("image-lazy-loading");
        img.removeEventListener("load", onLoad);
        img.removeEventListener("error", onLoad);
      };
      img.addEventListener("load", onLoad);
      img.addEventListener("error", onLoad);
    };

    if (!("IntersectionObserver" in window)) {
      images.forEach(applySrcset);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          applySrcset(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "100px 0px" },
    );

    images.forEach((img) => observer.observe(img));
  }

  // Detect browser RTL scrollLeft behavior once per document and cache it.
  // Possible values:
  // - default:  scrollLeft increases from 0..max (LTR-like)
  // - reverse:  scrollLeft decreases from max..0
  // - negative: scrollLeft goes 0..-max
  function getRtlScrollMode() {
    const root = document.documentElement;
    const cached = root?.dataset?.rtlScrollMode;
    if (cached) return cached;
    if (!document.body) return "default";

    const tester = document.createElement("div");
    tester.dir = "rtl";
    tester.style.width = "4px";
    tester.style.height = "1px";
    tester.style.overflow = "scroll";
    tester.style.visibility = "hidden";
    tester.innerHTML = '<div style="width:8px;height:1px;"></div>';
    document.body.appendChild(tester);

    // If setting scrollLeft to 1 doesn't change scrollLeft, we're in "negative".
    // If scrollLeft at 0 is > 0, we're in "reverse".
    tester.scrollLeft = 0;
    const atZero = tester.scrollLeft;
    tester.scrollLeft = 1;
    const atOne = tester.scrollLeft;

    let mode = "default";
    if (atOne === 0) {
      mode = "negative";
    } else if (atZero > 0) {
      mode = "reverse";
    }

    document.body.removeChild(tester);
    if (root?.dataset) root.dataset.rtlScrollMode = mode;
    return mode;
  }

  // Add product to browsing history
  function addProductToHistory(productId, limit = 20) {
    if (!productId) return;
    const storageKey = "product_history_ids";
    try {
      const raw = localStorage.getItem(storageKey);
      const existing = raw ? JSON.parse(raw) : [];
      const list = Array.isArray(existing) ? existing.map(String) : [];
      const currentId = String(productId);
      const next = [currentId, ...list.filter((id) => id !== currentId)].slice(
        0,
        limit,
      );
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch (error) {
      // ignore storage errors
    }
  }

  /**
   * Get fetch config
   * @param {string} [type="json"] Response type
   * @param {string} [method="POST"] HTTP method
   * @returns {Object} Fetch config object
   */
  function fetchConfig(type = "json", method = "POST") {
    return {
      method: method,
      headers: {
        "Content-Type": "application/json",
        Accept: `application/${type}`,
      },
    };
  }

  /**
   * Check if a value is numeric
   * @param value
   * @returns {boolean}
   */
  function isNumeric(value) {
    if (value === null || value === "") return false;
    return !Number.isNaN(Number(value));
  }

  /**
   * Format price amount
   * @param {number} [amount=0]
   * @param {string} currency
   * @returns {string|undefined}
   */
  function formatPriceAmount(amount = 0, currency) {
    if (!window.priceFormatTemplate || !currency) return;

    const value = amount / 100;
    const hasNoDecimals = NO_DECIMAL_CURRENCIES.includes(currency);

    const price = value.toLocaleString(undefined, {
      minimumFractionDigits: hasNoDecimals ? 0 : 2,
      maximumFractionDigits: hasNoDecimals ? 0 : 2,
    });

    return window.priceFormatTemplate.replace(/[\d,.]+/, price);
  }

  // Localization details element
  function initLocalizationDetails() {
    const detailsList = Array.from(
      document.querySelectorAll("details[data-localization-details]"),
    );
    if (!detailsList.length) return;

    const closeAll = (except) => {
      detailsList.forEach((details) => {
        if (details !== except) {
          details.removeAttribute("open");
        }
      });
    };

    document.addEventListener("click", (event) => {
      const current = event.target.closest(
        "details[data-localization-details]",
      );
      if (current) {
        closeAll(current);
        return;
      }
      closeAll(null);
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closeAll(null);
    });
  }

  // Tooltip hover helper
  function hideTooltips() {
    const tooltip = document.body?.querySelector("[data-tooltip-root]");
    if (!tooltip) return;
    tooltip.style.opacity = "0";
  }

  function initTooltips(root = document) {
    // Mobile/touch devices don't have a true hover state; avoid showing tooltips there.
    // This also prevents focus-triggered tooltips on touch screens.
    const canHover =
      window.matchMedia &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    if (!canHover) {
      hideTooltips();
      return;
    }

    const targets = Array.from(root.querySelectorAll("[data-tooltip]"));
    if (!targets.length) return;

    let tooltip = document.body.querySelector("[data-tooltip-root]");
    if (!tooltip) {
      tooltip = document.createElement("div");
      tooltip.setAttribute("data-tooltip-root", "true");
      tooltip.className = "tooltip";

      const arrow = document.createElement("div");
      arrow.className = "tooltip__arrow";
      tooltip.appendChild(arrow);

      document.body.appendChild(tooltip);
    }

    let hideTimer = null;
    const showTooltip = (event) => {
      const target = event.currentTarget;
      const text = target.dataset.tooltip || "";
      if (!text) return;
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
      tooltip.textContent = text;
      const arrow = tooltip.querySelector("div");
      if (arrow) tooltip.appendChild(arrow);
      tooltip.style.opacity = "1";
      positionTooltip(target);
    };

    const hideTooltip = () => {
      if (hideTimer) {
        clearTimeout(hideTimer);
      }
      hideTimer = setTimeout(() => {
        tooltip.style.opacity = "0";
      }, 50);
    };

    const positionTooltip = (target) => {
      const rect = target.getBoundingClientRect();
      const offset = 12;
      const top = rect.top - offset;
      tooltip.style.left = `${rect.left + rect.width / 2}px`;
      tooltip.style.top = `${top}px`;
      tooltip.style.transform = "translate(-50%, -100%)";
    };

    targets.forEach((target) => {
      if (target.dataset.tooltipInit === "true") return;
      target.dataset.tooltipInit = "true";
      target.addEventListener("mouseenter", showTooltip);
      target.addEventListener("mouseleave", hideTooltip);
      target.addEventListener("mousemove", () => positionTooltip(target));
    });
  }

  function escapeAttributeValue(value) {
    return String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  }

  function resolveShopifyEditorBlockId(event, root = document) {
    if (!event) return null;

    const blockIdFromDetail =
      event.detail && typeof event.detail.blockId === "string"
        ? event.detail.blockId
        : null;
    if (blockIdFromDetail) {
      return blockIdFromDetail;
    }

    const target = event.target instanceof Element ? event.target : null;
    if (!target) {
      return null;
    }

    const scope = root instanceof Element ? root : null;
    const isInScope = (node) => !scope || scope.contains(node);

    const nodeWithData = target.closest("[data-block-id]");
    if (nodeWithData && isInScope(nodeWithData)) {
      return nodeWithData.dataset.blockId || null;
    }

    const nodeWithShopifyData = target.closest("[data-shopify-editor-block-id]");
    if (nodeWithShopifyData && isInScope(nodeWithShopifyData)) {
      return nodeWithShopifyData.dataset.shopifyEditorBlockId || null;
    }

    const nodeWithId = target.closest('[id^="shopify-block-"]');
    if (nodeWithId && isInScope(nodeWithId)) {
      return nodeWithId.id.replace("shopify-block-", "");
    }

    return null;
  }

  /**
   * Bind Shopify Theme Editor block select/deselect events for a specific scope.
   * Resolves block id + element, then dispatches normalized payload to callbacks.
   * @param {Object} [options]
   * @param {Element|Document} [options.root=document] Query scope for matching block element.
   * @param {(payload:{blockId:string,event:Event,blockElement:Element}) => void} [options.onSelect]
   * @param {(payload:{blockId:string,event:Event,blockElement:Element}) => void} [options.onDeselect]
   * @param {(blockId:string) => Element|null} [options.getBlockElement]
   * @returns {() => void} Unbind function.
   */
  function bindShopifyEditorBlockEvents(options = {}) {
    const root = options.root instanceof Element ? options.root : document;
    const onSelect =
      typeof options.onSelect === "function" ? options.onSelect : null;
    const onDeselect =
      typeof options.onDeselect === "function" ? options.onDeselect : null;
    const getBlockElement =
      typeof options.getBlockElement === "function"
        ? options.getBlockElement
        : function defaultGetBlockElement(blockId) {
            if (!blockId || !root.querySelector) return null;
            const escapedBlockId = escapeAttributeValue(blockId);
            if (
              root instanceof Element &&
              (root.id === `shopify-block-${blockId}` ||
                root.dataset.blockId === blockId ||
                root.dataset.shopifyEditorBlockId === blockId)
            ) {
              return root;
            }
            const blockById = document.getElementById(
              `shopify-block-${blockId}`,
            );
            if (blockById && root.contains(blockById)) {
              return blockById;
            }
            return (
              root.querySelector(
                `[data-block-id="${escapedBlockId}"], [data-shopify-editor-block-id="${escapedBlockId}"]`,
              )
            );
          };

    const emit = (callback, event) => {
      if (!callback) return;
      const blockId = resolveShopifyEditorBlockId(event, root);
      if (!blockId) return;
      const blockElement = getBlockElement(blockId);
      if (!blockElement) return;
      callback({ blockId: blockId, event: event, blockElement: blockElement });
    };

    const handleSelect = (event) => emit(onSelect, event);
    const handleDeselect = (event) => emit(onDeselect, event);

    document.addEventListener("shopify:block:select", handleSelect);
    document.addEventListener("shopify:block:deselect", handleDeselect);
    window.addEventListener("shopify:block:select", handleSelect);
    window.addEventListener("shopify:block:deselect", handleDeselect);

    return function unbindShopifyEditorBlockEvents() {
      document.removeEventListener("shopify:block:select", handleSelect);
      document.removeEventListener("shopify:block:deselect", handleDeselect);
      window.removeEventListener("shopify:block:select", handleSelect);
      window.removeEventListener("shopify:block:deselect", handleDeselect);
    };
  }

  onReady(initLazyImageAnimation);
  onReady(initLocalizationDetails);
  onReady(initTooltips);
  document.addEventListener("shopify:section:load", initLazyImageAnimation);
  document.addEventListener("shopify:section:load", initLocalizationDetails);
  document.addEventListener("shopify:section:load", initTooltips);
  document.addEventListener("shopify:section:select", initLazyImageAnimation);
  document.addEventListener("shopify:section:reorder", initLazyImageAnimation);
  document.addEventListener("shopify:section:deselect", initLazyImageAnimation);

  /**
   * Get all visible, focusable elements inside a container
   * @param {HTMLElement} container
   * @returns {HTMLElement[]}
   */
  function getFocusableElements(container) {
    if (!container) return [];
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(
      (el) =>
        !el.closest("[hidden]") &&
        !el.closest(".hidden") &&
        el.offsetParent !== null
    );
  }

  /**
   * Create a keydown handler that traps Tab focus within a container
   * and optionally closes on Escape.
   * @param {HTMLElement} container
   * @param {object} [options]
   * @param {function} [options.onEscape] - Called when Escape is pressed
   * @returns {function}
   */
  function createFocusTrap(container, options) {
    var onEscape = options && options.onEscape ? options.onEscape : null;
    return function (event) {
      if (event.key === "Escape" && onEscape) {
        event.preventDefault();
        onEscape();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = getFocusableElements(container);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
  }

  /**
   * Move focus to the first focusable element inside a container.
   * @param {HTMLElement} container
   */
  function focusFirstElement(container) {
    if (!container) return;
    requestAnimationFrame(function () {
      var focusable = getFocusableElements(container);
      if (focusable.length) {
        focusable[0].focus();
      }
    });
  }

  return {
    fetchHtml: fetchHtml,
    initLazyImageAnimation: initLazyImageAnimation,
    initLocalizationDetails: initLocalizationDetails,
    initTooltips: initTooltips,
    hideTooltips: hideTooltips,
    getRtlScrollMode: getRtlScrollMode,
    fetchConfig: fetchConfig,
    isNumeric: isNumeric,
    formatPriceAmount: formatPriceAmount,
    addProductToHistory: addProductToHistory,
    getFocusableElements: getFocusableElements,
    createFocusTrap: createFocusTrap,
    focusFirstElement: focusFirstElement,
    resolveShopifyEditorBlockId: resolveShopifyEditorBlockId,
    bindShopifyEditorBlockEvents: bindShopifyEditorBlockEvents,
  };
})();

window.webvista = webvista;

// Header Component

// ── Base Components ──────────────────────────────────────────────
// Handles header interactions like mega menus, dropdowns, and mobile menu
class HeaderComponent extends HTMLElement {
  constructor() {
    super();
    this.header = this;
    this.activeMega = null;
    this.activeDropdown = null;
    this.mobileOpen = false;
    this.searchOpen = false;
    this.isStickyActive = false;
    this.stickyAnimTimer = null;
    this.lastScrollY = 0;
    this.cleanup = [];
    this.isReady = false;
    this.scrollRaf = null;
    this.resizeRaf = null;
    this.isScrolled = null;
    this.isTransparentState = null; // Transparent state
    this.menuOpenState = null;
    this.hasMegaState = null;
    this.activeMegaState = null;
    this.activeDropdownState = null;
    this.mobileOpenState = null;
    this.searchOpenState = null;
    this.mobileSubmenuState = "";
    this.scrollLockState = null;
    this.stickyInsetState = null;
    this.headerHeightState = null;
    this.stickyTransitionState = null;
    this.headerBarHeight = 0;
    this.openMobileSubmenus = new Set();
    this.submenuAutoTimers = new WeakMap();
    this.submenuHeights = new WeakMap();
    this.searchOpener = null;
    this._searchFocusTrap = null;
    this.mobileOpener = null;
    this._mobileFocusTrap = null;
  }

  connectedCallback() {
    if (this.isReady) return;
    this.isReady = true;
    this.cacheElements();
    this.bindEvents();
    this.update();
  }

  disconnectedCallback() {
    this.destroy();
    this.isReady = false;
  }

  // Cache element references
  cacheElements() {
    this.headerBar = this.querySelector("[data-header-bar]");
    this.logoText = this.querySelector("[data-logo-text]");
    this.logoImage = this.querySelector("[data-logo-image]");
    this.logoImageTransparent = this.querySelector(
      "[data-logo-image-transparent]",
    );
    this.logoImages = Array.from(
      this.querySelectorAll("[data-logo-image]"),
    );
    this.logoImagesTransparent = Array.from(
      this.querySelectorAll("[data-logo-image-transparent]"),
    );
    this.navRow = this.querySelector("[data-header-nav]");
    this.cartBadge = this.querySelector("[data-cart-badge]");
    this.megaWrapper = this.querySelector("[data-mega-wrapper]");
    this.megaPanels = Array.from(this.querySelectorAll("[data-mega-panel]"));
    this.megaTriggers = Array.from(
      this.querySelectorAll("[data-mega-trigger]"),
    );
    this.searchOverlay = document.querySelector("[data-search-overlay]");
    this.searchInput = this.searchOverlay
      ? this.searchOverlay.querySelector("[data-search-input]")
      : null;
    this.searchToggles = Array.from(
      this.querySelectorAll("[data-search-toggle]"),
    );
    this.searchCloseButtons = this.searchOverlay
      ? Array.from(this.searchOverlay.querySelectorAll("[data-search-close]"))
      : [];
    this.cartDrawer = document.getElementById("Cart-Drawer");
    this.cartToggles = Array.from(
      this.querySelectorAll("[data-cart-drawer-toggle]"),
    );
    this.dropdownButtons = Array.from(
      this.querySelectorAll("[data-dropdown-toggle]"),
    );
    this.dropdownPanels = Array.from(
      this.querySelectorAll("[data-dropdown-panel]"),
    );
    this.mobileToggles = Array.from(
      this.querySelectorAll("[data-mobile-toggle]"),
    );
    this.mobileDrawer = this.querySelector("[data-mobile-drawer]");
    this.mobileBackdrop = this.querySelector("[data-mobile-backdrop]");
    this.mobilePanel = this.querySelector("[data-mobile-panel]");
    this.mobileMenu = this.querySelector("[data-mobile-menu]");
    this.mobileSubmenuToggles = Array.from(
      this.querySelectorAll("[data-mobile-submenu-toggle]"),
    );
    this.mobileSubmenus = Array.from(
      this.querySelectorAll("[data-mobile-submenu]"),
    );
    this.sectionWrapper = this.closest(".shopify-section");
    this.transparentHeader = this.dataset.transparentHeader === "true"; // Whether transparent header is enabled
    this.cartType = this.dataset.cartType || "drawer";
    this.stickyType = this.dataset.stickyType || "none";
    if (this.stickyType === "reduce-logo-size") {
      this.stickyType = "none";
    }
    this.headerBarHeight = this.headerBar ? this.headerBar.offsetHeight : 0;
  }

  addListener(el, event, handler, options) {
    if (!el) return;
    el.addEventListener(event, handler, options);
    this.cleanup.push(() => el.removeEventListener(event, handler, options));
  }

  toggleClasses(el, classes, on) {
    if (!el) return;
    classes.forEach((cls) => el.classList.toggle(cls, on));
  }

  // Toggle sticky section classes
  toggleStickySectionClasses(on) {
    if (!this.sectionWrapper) return;
    this.sectionWrapper.classList.toggle("sticky", on);
    this.sectionWrapper.classList.toggle("top-0", on);
    this.sectionWrapper.classList.toggle("z-50", on);
  }

  // Update header state based on scroll position and interaction
  update() {
    if (!this.header.isConnected) return;

    const scrollY = window.scrollY || 0;
    const isScrolled = scrollY > 8; // Scrolled past threshold
    const scrollUp = scrollY < this.lastScrollY;
    const stickyThreshold = this.headerBarHeight || 0;
    const hasMega = Boolean(this.activeMega);
    const hasDropdown = Boolean(this.activeDropdown);
    const menuOpen =
      hasMega || hasDropdown || this.mobileOpen || this.searchOpen;
    const isTransparent = this.transparentHeader && !menuOpen && !isScrolled; // Should be transparent
    const shadowOn =
      !isTransparent || hasDropdown || this.mobileOpen || this.searchOpen;
    const openMobileKey = Array.from(this.openMobileSubmenus).sort().join("|");
    const isTransparentChanged = isTransparent !== this.isTransparentState;
    const isScrolledChanged = isScrolled !== this.isScrolled;
    const shadowOnChanged = shadowOn !== this.shadowOnState;
    const hasMegaChanged = hasMega !== this.hasMegaState;
    const activeMegaChanged = this.activeMega !== this.activeMegaState;
    const activeDropdownChanged =
      this.activeDropdown !== this.activeDropdownState;
    const mobileOpenChanged = this.mobileOpen !== this.mobileOpenState;
    const searchOpenChanged = this.searchOpen !== this.searchOpenState;
    const submenusChanged = openMobileKey !== this.mobileSubmenuState;

    this.updateTransparency({
      isTransparent,
      isTransparentChanged,
      isScrolled,
      isScrolledChanged,
    });
    this.updateMenus({
      hasMega,
      shadowOn,
      shadowOnChanged,
      hasMegaChanged,
      activeMegaChanged,
      activeDropdownChanged,
    });
    this.updateMobile({
      openMobileKey,
      mobileOpenChanged,
      submenusChanged,
    });
    this.updateSticky({
      scrollY,
      scrollUp,
      stickyThreshold,
      menuOpen,
      searchOpen: this.searchOpen,
    });
    this.updateSearchOverlay({ searchOpenChanged });
    this.updateScrollLock({ searchOpenChanged, mobileOpenChanged });

    this.isScrolled = isScrolled;
    this.lastScrollY = scrollY;
    this.isTransparentState = isTransparent;
    this.menuOpenState = menuOpen;
    this.shadowOnState = shadowOn;
    this.hasMegaState = hasMega;
    this.activeMegaState = this.activeMega;
    this.activeDropdownState = this.activeDropdown;
    this.mobileOpenState = this.mobileOpen;
    this.searchOpenState = this.searchOpen;
    this.mobileSubmenuState = openMobileKey;
  }

  // Update transparency
  updateTransparency({
    isTransparent,
    isTransparentChanged,
    isScrolled,
    isScrolledChanged,
  }) {
    if (!this.transparentHeader) return;

    if (isTransparentChanged) {
      if (isTransparent) {
        this.header.setAttribute("data-header-transparent", "true");
      } else {
        this.header.removeAttribute("data-header-transparent");
      }

      // Toggle logo visibility based on transparent state
      if (this.logoImagesTransparent.length) {
        this.logoImagesTransparent.forEach((el) => {
          this.toggleClasses(el, ["hidden"], !isTransparent);
        });
        this.logoImages.forEach((el) => {
          this.toggleClasses(el, ["hidden"], isTransparent);
        });
      }
    }

    if (isScrolledChanged) {
      this.toggleClasses(this.headerBar, ["py-2"], isScrolled);
      this.toggleClasses(this.headerBar, ["py-6"], !isScrolled);
      this.toggleClasses(this.logoText, ["text-xl", "md:text-2xl"], isScrolled);
      this.toggleClasses(
        this.logoText,
        ["text-2xl", "md:text-4xl"],
        !isScrolled,
      );
      [...this.logoImages, ...this.logoImagesTransparent].forEach((logo) => {
        this.toggleClasses(logo, ["h-6", "md:h-8"], isScrolled);
        this.toggleClasses(logo, ["h-8", "md:h-12"], !isScrolled);
      });
      this.headerBarHeight = this.headerBar ? this.headerBar.offsetHeight : 0;
    }
  }

  // Update menu state
  updateMenus({
    hasMega,
    shadowOn,
    shadowOnChanged,
    hasMegaChanged,
    activeMegaChanged,
    activeDropdownChanged,
  }) {
    if (
      !shadowOnChanged &&
      !hasMegaChanged &&
      !activeMegaChanged &&
      !activeDropdownChanged
    ) {
      return;
    }

    if (shadowOnChanged) {
      this.toggleClasses(this.headerBar, ["shadow-sm"], shadowOn);
    }

    if (hasMegaChanged && this.megaWrapper) {
      this.toggleClasses(
        this.megaWrapper,
        ["opacity-100", "pointer-events-auto", "max-h-[600px]"],
        hasMega,
      );
      this.toggleClasses(
        this.megaWrapper,
        ["opacity-0", "pointer-events-none", "max-h-0"],
        !hasMega,
      );
      this.megaWrapper.inert = !hasMega;
    }

    if (activeMegaChanged) {
      this.megaPanels.forEach((panel) => {
        const isHidden = panel.dataset.megaPanel !== this.activeMega;
        panel.classList.toggle("hidden", isHidden);
        panel.classList.toggle("grid", !isHidden);
      });

      this.megaTriggers.forEach((trigger) => {
        const id = trigger.dataset.megaTrigger;
        const isActive = id === this.activeMega;
        const link = trigger.querySelector("[data-mega-link]");
        const arrow = trigger.querySelector("[data-mega-arrow]");
        const underline = trigger.querySelector("[data-mega-underline]");

        this.toggleClasses(link, ["accent"], isActive);
        this.toggleClasses(arrow, ["rotate-180"], isActive);
        this.toggleClasses(underline, ["w-full"], isActive);
        this.toggleClasses(underline, ["w-0"], !isActive);
      });
    }

    if (activeDropdownChanged) {
      this.dropdownPanels.forEach((panel) => {
        panel.classList.toggle(
          "hidden",
          panel.dataset.dropdownPanel !== this.activeDropdown,
        );
      });

      this.dropdownButtons.forEach((button) => {
        const id = button.dataset.dropdownToggle;
        const isActive = id === this.activeDropdown;
        button.setAttribute("aria-expanded", isActive ? "true" : "false");
        const arrow = button.querySelector("[data-dropdown-arrow]");
        this.toggleClasses(arrow, ["rotate-180"], isActive);
      });
    }
  }

  // Update mobile menu state
  updateMobile({ openMobileKey, mobileOpenChanged, submenusChanged }) {
    if (!mobileOpenChanged && !submenusChanged) return;

    if (mobileOpenChanged) {
      if (this.mobileDrawer) {
        this.toggleClasses(
          this.mobileDrawer,
          ["opacity-100", "pointer-events-auto"],
          this.mobileOpen,
        );
        this.toggleClasses(
          this.mobileDrawer,
          ["opacity-0", "pointer-events-none"],
          !this.mobileOpen,
        );
      }
      if (this.mobileBackdrop) {
        this.toggleClasses(
          this.mobileBackdrop,
          ["opacity-100"],
          this.mobileOpen,
        );
        this.toggleClasses(
          this.mobileBackdrop,
          ["opacity-0"],
          !this.mobileOpen,
        );
      }
      if (this.mobilePanel) {
        this.toggleClasses(
          this.mobilePanel,
          ["translate-x-0"],
          this.mobileOpen,
        );
        this.toggleClasses(
          this.mobilePanel,
          ["-translate-x-full"],
          !this.mobileOpen,
        );
      }
    }

    if (mobileOpenChanged && this.mobileToggles.length) {
      this.mobileToggles.forEach((toggle) => {
        toggle.setAttribute(
          "aria-expanded",
          this.mobileOpen ? "true" : "false",
        );
      });
    }

    if (mobileOpenChanged && this.mobileDrawer) {
      if (this.mobileOpen) {
        if (!this._mobileFocusTrap) {
          this._mobileFocusTrap = webvista.createFocusTrap(this.mobileDrawer, {
            onEscape: () => {
              this.mobileOpen = false;
              this.openMobileSubmenus.clear();
              this.update();
            },
          });
        }
        this.mobileDrawer.inert = false;
        this.mobileDrawer.addEventListener("keydown", this._mobileFocusTrap);
        webvista.focusFirstElement(this.mobilePanel);
      } else {
        this.mobileDrawer.inert = true;
        if (this._mobileFocusTrap) {
          this.mobileDrawer.removeEventListener("keydown", this._mobileFocusTrap);
        }
        if (this.mobileOpener && typeof this.mobileOpener.focus === "function") {
          this.mobileOpener.focus();
          this.mobileOpener = null;
        }
      }
    }

    if (submenusChanged && this.mobileSubmenus.length) {
      this.mobileSubmenus.forEach((panel) => {
        const id = panel.dataset.mobileSubmenu;
        const isOpen = this.openMobileSubmenus.has(id);
        panel.inert = !isOpen;
        let cachedHeight = this.submenuHeights.get(panel);

        if (isOpen) {
          panel.style.opacity = "1";
          if (panel.style.maxHeight !== "none") {
            if (!cachedHeight) {
              cachedHeight = panel.scrollHeight;
              this.submenuHeights.set(panel, cachedHeight);
            }
            panel.style.maxHeight = `${cachedHeight}px`;
            this.deferSubmenuAutoHeight(panel, id);
          }
        } else {
          panel.style.opacity = "0";
          if (panel.style.maxHeight === "none") {
            if (!cachedHeight) {
              cachedHeight = panel.scrollHeight;
              this.submenuHeights.set(panel, cachedHeight);
            }
            panel.style.maxHeight = `${cachedHeight}px`;
          }
          requestAnimationFrame(() => {
            panel.style.maxHeight = "0px";
          });
        }
      });
    }

    if (submenusChanged && this.mobileSubmenuToggles.length) {
      this.mobileSubmenuToggles.forEach((toggle) => {
        const id = toggle.dataset.mobileSubmenuToggle;
        const isOpen = this.openMobileSubmenus.has(id);
        toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
        const arrow = toggle.querySelector("[data-mobile-submenu-arrow]");
        this.toggleClasses(arrow, ["rotate-180"], isOpen);
      });
    }
  }

  // Update sticky header state
  updateSticky({ scrollY, scrollUp, stickyThreshold, menuOpen, searchOpen }) {
    if (!this.sectionWrapper) return;

    if (
      searchOpen &&
      (this.stickyType === "always" || this.stickyType === "on-scroll-up")
    ) {
      if (!this.isStickyActive) {
        this.toggleStickySectionClasses(true);
        this.isStickyActive = true;
      }
      if (this.stickyType === "on-scroll-up") {
        const headerHeight = stickyThreshold || 0;
        if (headerHeight !== this.headerHeightState) {
          this.headerHeightState = headerHeight;
        }
        if (!this.stickyTransitionState) {
          this.sectionWrapper.style.setProperty(
            "transition",
            "inset-block-start 300ms ease",
          );
          this.stickyTransitionState = true;
        }
        if (this.stickyInsetState !== "0px") {
          this.sectionWrapper.style.setProperty("inset-block-start", "0px");
          this.stickyInsetState = "0px";
        }
      }
      return;
    }

    if (this.stickyType === "always") {
      if (!this.isStickyActive) {
        this.toggleStickySectionClasses(true);
        this.isStickyActive = true;
      }
      return;
    }

    if (this.stickyType === "on-scroll-up") {
      if (!this.isStickyActive) {
        this.toggleStickySectionClasses(true);
        this.isStickyActive = true;
      }

      const headerHeight = stickyThreshold || 0;
      if (headerHeight !== this.headerHeightState) {
        this.headerHeightState = headerHeight;
      }

      // Set sticky transition animation
      if (!this.stickyTransitionState) {
        this.sectionWrapper.style.setProperty(
          "transition",
          "inset-block-start 300ms ease",
        );
        this.stickyTransitionState = true;
      }

      const shouldHide = !scrollUp && !menuOpen;
      const insetValue = shouldHide ? `-${headerHeight}px` : "0px";
      if (insetValue !== this.stickyInsetState) {
        this.sectionWrapper.style.setProperty("inset-block-start", insetValue);
        this.stickyInsetState = insetValue;
      }
      return;
    }

    if (this.isStickyActive) {
      this.toggleStickySectionClasses(false);
      this.isStickyActive = false;
    }
    if (this.stickyInsetState !== null) {
      this.sectionWrapper.style.removeProperty("inset-block-start");
      this.stickyInsetState = null;
    }
    if (this.headerHeightState !== null) {
      this.headerHeightState = null;
    }
  }

  // Update search overlay state
  updateSearchOverlay({ searchOpenChanged }) {
    if (!this.searchOverlay) return;
    if (!searchOpenChanged) return;

    this.toggleClasses(
      this.searchOverlay,
      ["translate-y-0", "opacity-100", "pointer-events-auto"],
      this.searchOpen,
    );
    this.toggleClasses(
      this.searchOverlay,
      ["-translate-y-full", "opacity-0", "pointer-events-none"],
      !this.searchOpen,
    );
    this.searchOverlay.inert = !this.searchOpen;
    this.searchOverlay.setAttribute("aria-hidden", String(!this.searchOpen));
  }

  // Update scroll lock state
  updateScrollLock({ searchOpenChanged, mobileOpenChanged }) {
    const shouldLock = this.mobileOpen || this.searchOpen;
    if (!searchOpenChanged && !mobileOpenChanged) return;
    if (shouldLock === this.scrollLockState) return;
    if (shouldLock) {
      this._savedScrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${this._savedScrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.documentElement.classList.add("overflow-hidden");
    } else {
      document.documentElement.classList.remove("overflow-hidden");
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      if (typeof this._savedScrollY === "number") {
        window.scrollTo(0, this._savedScrollY);
        this._savedScrollY = null;
      }
    }
    this.scrollLockState = shouldLock;
  }

  deferSubmenuAutoHeight(panel, id) {
    const existingTimer = this.submenuAutoTimers.get(panel);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }
    const timer = setTimeout(() => {
      if (this.openMobileSubmenus.has(id)) {
        panel.style.maxHeight = "none";
      }
    }, 520);
    this.submenuAutoTimers.set(panel, timer);
  }

  openMega(id) {
    if (!id || !this.megaPanels.length) return;
    this.activeMega = id;
    this.activeDropdown = null;
    this.mobileOpen = false;
    this.update();
  }

  closeMega() {
    if (!this.activeMega) return;
    this.returnMegaFocus();
    this.activeMega = null;
    this.update();
  }

  returnMegaFocus() {
    if (!this.activeMega) return;
    const active = document.activeElement;
    const panel = this.megaPanels.find(
      (p) => p.dataset.megaPanel === this.activeMega,
    );
    if (!panel || !panel.contains(active)) return;
    const trigger = this.megaTriggers.find(
      (t) => t.dataset.megaTrigger === this.activeMega,
    );
    const link = trigger?.querySelector("[data-mega-link]");
    if (link) {
      link.focus();
    }
  }

  openSearch(opener) {
    if (!this.searchOverlay) return;
    this.searchOpener = opener || document.activeElement;
    this.activeMega = null;
    this.activeDropdown = null;
    this.mobileOpen = false;
    this.searchOpen = true;
    this.update();
    document.dispatchEvent(new CustomEvent("search:open"));
    if (!this._searchFocusTrap) {
      this._searchFocusTrap = webvista.createFocusTrap(this.searchOverlay);
    }
    this.searchOverlay.addEventListener("keydown", this._searchFocusTrap);
    if (this.searchInput) {
      requestAnimationFrame(() => {
        this.searchInput.focus();
      });
    }
  }

  closeSearch() {
    if (!this.searchOverlay) return;
    this.searchOpen = false;
    this.update();
    if (this._searchFocusTrap) {
      this.searchOverlay.removeEventListener("keydown", this._searchFocusTrap);
    }
    if (this.searchOpener && typeof this.searchOpener.focus === "function") {
      this.searchOpener.focus();
      this.searchOpener = null;
    }
  }

  toggleSearch(opener) {
    if (!this.searchOverlay) return;
    if (this.searchOpen) {
      this.closeSearch();
    } else {
      this.openSearch(opener);
    }
  }

  toggleDropdown(id, options = {}) {
    const { keepMobileOpen = false } = options;
    this.activeMega = null;
    if (!keepMobileOpen) {
      this.mobileOpen = false;
    }
    this.activeDropdown = this.activeDropdown === id ? null : id;
    this.update();
  }

  closeDropdown() {
    if (!this.activeDropdown) return;
    this.activeDropdown = null;
    this.update();
  }

  closeAll() {
    if (this.searchOpen) {
      this.closeSearch();
    }
    if (this.mobileOpen) {
      this.mobileOpen = false;
      this.openMobileSubmenus.clear();
    }
    this.returnMegaFocus();
    this.activeMega = null;
    this.activeDropdown = null;
    this.update();
  }

  toggleMobileSubmenu(id) {
    if (!id) return;
    if (this.openMobileSubmenus.has(id)) {
      this.openMobileSubmenus.delete(id);
    } else {
      this.openMobileSubmenus.add(id);
    }
    this.update();
  }

  bindEvents() {
    this.megaTriggers.forEach((trigger) => {
      const id = trigger.dataset.megaTrigger;
      const panel = this.megaPanels.find(
        (p) => p.dataset.megaPanel === id,
      );
      const link = trigger.querySelector("[data-mega-link]");

      this.addListener(trigger, "mouseenter", () => this.openMega(id));
      this.addListener(trigger, "focusin", () => this.openMega(id));

      /* 焦点离开 trigger + panel 范围时关闭 mega */
      const checkMegaFocusLost = () => {
        requestAnimationFrame(() => {
          if (this.activeMega !== id) return;
          const active = document.activeElement;
          if (trigger.contains(active)) return;
          if (panel && panel.contains(active)) return;
          this.activeMega = null;
          this.update();
        });
      };
      this.addListener(trigger, "focusout", checkMegaFocusLost);
      if (panel) this.addListener(panel, "focusout", checkMegaFocusLost);

      /* Tab: trigger → panel 第一个链接 */
      if (link) {
        this.addListener(link, "keydown", (event) => {
          if (event.key !== "Tab" || event.shiftKey) return;
          if (this.activeMega !== id || !panel) return;
          event.preventDefault();
          webvista.focusFirstElement(panel);
        });
      }

      /* Tab/Shift+Tab: panel 边界处理 */
      if (panel) {
        this.addListener(panel, "keydown", (event) => {
          if (event.key !== "Tab") return;
          const focusable = webvista.getFocusableElements(panel);
          if (!focusable.length) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];

          if (!event.shiftKey && document.activeElement === last) {
            /* Tab 过最后一个链接 → 焦点到下一个导航项 */
            event.preventDefault();
            this.activeMega = null;
            this.update();
            const nextItem = trigger.nextElementSibling;
            const nextFocusable = nextItem
              ? nextItem.querySelector("a, button") || nextItem
              : null;
            if (nextFocusable) nextFocusable.focus();
          } else if (event.shiftKey && document.activeElement === first) {
            /* Shift+Tab 过第一个链接 → 焦点回 trigger（mega 保持打开） */
            event.preventDefault();
            if (link) link.focus();
          }
        });
      }
    });

    this.dropdownButtons.forEach((button) => {
      const id = button.dataset.dropdownToggle;
      this.addListener(button, "click", (event) => {
        event.preventDefault();
        const isMobileDropdown = Boolean(button.closest("[data-mobile-panel]"));
        this.toggleDropdown(id, { keepMobileOpen: isMobileDropdown });
      });
    });

    if (this.searchToggles.length) {
      this.searchToggles.forEach((toggle) => {
        this.addListener(toggle, "click", (event) => {
          event.preventDefault();
          this.toggleSearch(toggle);
        });
      });
    }

    if (this.searchCloseButtons.length) {
      this.searchCloseButtons.forEach((button) => {
        this.addListener(button, "click", () => {
          this.closeSearch();
        });
      });
    }

    if (this.cartToggles.length) {
      this.cartToggles.forEach((toggle) => {
        this.addListener(toggle, "click", (event) => {
          if (this.cartType === "page") return;
          event.preventDefault();
          if (this.cartDrawer) {
            this.cartDrawer.show(event.currentTarget);
            return;
          }
          window.location.href = window.routes?.cart_url || "/cart";
        });
      });
    }

    if (this.mobileToggles.length) {
      this.mobileToggles.forEach((toggle) => {
        this.addListener(toggle, "click", () => {
          this.activeMega = null;
          this.activeDropdown = null;
          if (!this.mobileOpen) {
            this.mobileOpener = toggle;
          }
          this.mobileOpen = !this.mobileOpen;
          if (!this.mobileOpen) {
            this.openMobileSubmenus.clear();
          }
          this.update();
        });
      });
    }

    if (this.mobileBackdrop) {
      this.addListener(this.mobileBackdrop, "click", () => {
        this.activeMega = null;
        this.activeDropdown = null;
        this.mobileOpen = false;
        this.openMobileSubmenus.clear();
        this.update();
      });
    }

    if (this.mobilePanel) {
      this.addListener(this.mobilePanel, "click", (event) => {
        event.stopPropagation();
        if (
          this.activeDropdown &&
          !event.target.closest("[data-dropdown-panel]") &&
          !event.target.closest("[data-dropdown-toggle]")
        ) {
          this.closeDropdown();
        }
      });
      this.addListener(this.mobilePanel, "pointerdown", (event) => {
        event.stopPropagation();
      });
    }

    if (this.mobileSubmenuToggles.length) {
      this.mobileSubmenuToggles.forEach((toggle) => {
        const id = toggle.dataset.mobileSubmenuToggle;
        this.addListener(toggle, "click", (event) => {
          if (event.target.closest("[data-mobile-menu-link]")) return;
          event.preventDefault();
          this.toggleMobileSubmenu(id);
        });
        this.addListener(toggle, "keydown", (event) => {
          if (event.key === "Enter" || event.key === " ") {
            if (event.target.closest("[data-mobile-menu-link]")) return;
            event.preventDefault();
            this.toggleMobileSubmenu(id);
          }
        });
      });
    }

    this.addListener(this.header, "mouseleave", () => this.closeMega());
    this.megaTriggers.forEach((trigger) => {
  const navLinks = Array.from(
    this.querySelectorAll("[data-header-nav] a, [data-header-nav] button")
  ).filter((el) => !el.closest("[data-mega-trigger]"));
  navLinks.forEach((link) => {
    this.addListener(link, "mouseenter", () => this.closeMega());
  });
});
    this.addListener(this.header, "focusout", (event) => {
      if (this.mobileOpen) return;
      if (this.searchOpen) return;
      if (!this.header.contains(event.relatedTarget)) this.closeAll();
    });

    this.addListener(document, "click", (event) => {
      if (this.mobileOpen) return;
      if (this.searchOpen && this.searchOverlay?.contains(event.target)) return;
      if (!this.header.contains(event.target)) this.closeAll();
    });

    this.addListener(document, "keydown", (event) => {
      if (event.key === "Escape") this.closeAll();
    });

    const onScroll = () => {
      if (this.scrollRaf) return;
      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = null;
        this.update();
      });
    };
    this.addListener(window, "scroll", onScroll, { passive: true });

    const onResize = () => {
      if (this.resizeRaf) return;
      this.resizeRaf = requestAnimationFrame(() => {
        this.resizeRaf = null;
        this.headerBarHeight = this.headerBar ? this.headerBar.offsetHeight : 0;
        this.update();
      });
    };
    this.addListener(window, "resize", onResize);
  }

  destroy() {
    this.cleanup.forEach((remove) => remove());
    this.cleanup = [];
    if (this.scrollRaf) {
      cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = null;
    }
    if (this.resizeRaf) {
      cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = null;
    }
    this.submenuAutoTimers = new WeakMap();
    this.submenuHeights = new WeakMap();
  }
}
customElements.define("header-component", HeaderComponent);

// Slider component
class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.prevButton = null;
    this.nextButton = null;
    this.dots = null;
    this.scrollbar = null;
    this.scrollbarThumb = null;
    this.isEnabled = false;
    this.autoplayTimer = null;
    this.autoplayInterval = 5000;
    this.autoplayEnabled = false;
    this.autoplayPaused = false;
    this.pauseOnHover = true;
    this.prefersReducedMotion = null;
    this.onScroll = this.onScroll.bind(this);
    this.onPrevClick = this.onPrevClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onDotClick = this.onDotClick.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onSliderKeyDown = this.onSliderKeyDown.bind(this);
    this.onScrollbarPointerDown = this.onScrollbarPointerDown.bind(this);
    this.onScrollbarPointerMove = this.onScrollbarPointerMove.bind(this);
    this.onScrollbarPointerUp = this.onScrollbarPointerUp.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onDragStart = this.onDragStart.bind(this);
    this.resizeObserver = null;
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartScroll = 0;
    this.dragPointerId = null;
    this.prevScrollSnapType = "";
    this.prevScrollBehavior = "";
    this.snapRestoreTimer = null;
    this.dragSpeed = 1.6;
    this.dragRaf = null;
    this.dragTargetScroll = null;
    this.resetTransformTimer = null;
    this.edgeSnapTarget = null;
    this.dragDelta = 0;
    this.dragHasMoved = false;
    this.dragThreshold = 6;
    this.isScrollbarDragging = false;
    this.scrollbarPointerId = null;
    this.scrollbarDragStartX = 0;
    this.scrollbarDragStartScroll = 0;
    this.scrollbarRaf = null;
    this.scrollbarTargetScroll = null;
    this.scrollbarPrevSnapType = "";
    this.scrollbarPrevScrollBehavior = "";
    this.isRtl = null;
    this.isTouchCapable = null;
    this.editorCleanup = null;
  }

  connectedCallback() {
    this.track = this.querySelector("[data-slider-track]");
    this.prevButton = this.querySelector("[data-slider-prev]");
    this.nextButton = this.querySelector("[data-slider-next]");
    this.dots = this.querySelector("[data-slider-dots]");
    this.scrollbar = this.querySelector("[data-slider-scrollbar]");
    this.scrollbarThumb = this.querySelector("[data-slider-scrollbar-thumb]");

    if (!this.track) return;
    this.bindEditorEvents();

    const slides = Array.from(this.track.children);
    this.isEnabled = slides.length > 1;
    if (!this.isEnabled) {
      this.track.classList.remove("cursor-grab");
      this.track.classList.add("cursor-default");
      this.track.style.overflowX = "hidden";
      if (this.prevButton) this.prevButton.disabled = true;
      if (this.nextButton) this.nextButton.disabled = true;
      if (this.dots) {
        this.buildDots();
        this.updateDots();
      }
      if (this.scrollbar) {
        this.scrollbar.setAttribute("aria-hidden", "true");
        this.scrollbar.style.display = "none";
      }
      return;
    }

    this.track.querySelectorAll("img").forEach((img) => {
      img.setAttribute("draggable", "false");
      img.addEventListener("dragstart", this.onDragStart);
    });
    this.track.querySelectorAll("a").forEach((link) => {
      link.setAttribute("draggable", "false");
      link.addEventListener("dragstart", this.onDragStart);
    });

    const speedAttr = this.getAttribute("data-drag-speed");
    if (speedAttr) {
      const parsed = Number.parseFloat(speedAttr);
      if (!Number.isNaN(parsed) && parsed > 0) {
        this.dragSpeed = parsed;
      }
    }
    this.track.classList.add("cursor-grab");
    this.track.addEventListener("scroll", this.onScroll);
    this.track.addEventListener("pointerdown", this.onPointerDown);
    this.addEventListener("keydown", this.onSliderKeyDown);
    if (this.prevButton) {
      this.prevButton.addEventListener("click", this.onPrevClick);
    }
    if (this.nextButton) {
      this.nextButton.addEventListener("click", this.onNextClick);
    }
    if (this.dots) {
      this.dots.addEventListener("click", this.onDotClick);
      this.buildDots();
      this.updateDots();
    }
    if (this.scrollbar && this.scrollbarThumb) {
      this.scrollbar.addEventListener(
        "pointerdown",
        this.onScrollbarPointerDown,
      );
    }
    if (this.scrollbar) {
      this.scrollbar.setAttribute("aria-hidden", "false");
    }

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.track);
    }

    this.updateButtons();
    this.updateDots();
    this.updateScrollbar();
    this.setupAutoplay();
  }

  disconnectedCallback() {
    if (this.editorCleanup) {
      this.editorCleanup();
      this.editorCleanup = null;
    }
    if (this.track) {
      this.track.removeEventListener("scroll", this.onScroll);
      this.track.removeEventListener("pointerdown", this.onPointerDown);
      this.track.querySelectorAll("img").forEach((img) => {
        img.removeEventListener("dragstart", this.onDragStart);
      });
      this.track.querySelectorAll("a").forEach((link) => {
        link.removeEventListener("dragstart", this.onDragStart);
      });
    }
    this.removeEventListener("keydown", this.onSliderKeyDown);
    if (this.prevButton) {
      this.prevButton.removeEventListener("click", this.onPrevClick);
    }
    if (this.nextButton) {
      this.nextButton.removeEventListener("click", this.onNextClick);
    }
    if (this.dots) {
      this.dots.removeEventListener("click", this.onDotClick);
    }
    if (this.scrollbar) {
      this.scrollbar.removeEventListener(
        "pointerdown",
        this.onScrollbarPointerDown,
      );
    }
    this.removeScrollbarDragListeners();
    this.stopAutoplay();
    this.removeAutoplayListeners();
    this.removeDragListeners();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  onResize() {
    if (!this.isEnabled) return;
    this.updateButtons();
    this.updateDots();
    this.updateScrollbar();
  }

  onScroll() {
    if (!this.isEnabled) return;
    this.updateButtons();
    this.updateDots();
    this.updateScrollbar();
  }

  getIsRtl() {
    if (this.isRtl !== null) return this.isRtl;
    this.isRtl = document.documentElement.getAttribute("dir") === "rtl";
    return this.isRtl;
  }

  getIsTouchCapable() {
    if (this.isTouchCapable !== null) return this.isTouchCapable;
    const maxTouchPoints =
      typeof navigator !== "undefined" && navigator.maxTouchPoints
        ? navigator.maxTouchPoints
        : 0;
    if (maxTouchPoints > 0) {
      this.isTouchCapable = true;
      return this.isTouchCapable;
    }
    if (window.matchMedia) {
      // Some browsers report touch capability via coarse pointers.
      this.isTouchCapable = window.matchMedia("(any-pointer: coarse)").matches;
      return this.isTouchCapable;
    }
    this.isTouchCapable = false;
    return this.isTouchCapable;
  }

  getMaxScroll() {
    if (!this.track) return 0;
    return this.track.scrollWidth - this.track.clientWidth;
  }

  getScrollLeft() {
    if (!this.track) return 0;
    if (!this.getIsRtl()) return this.track.scrollLeft;
    const max = this.getMaxScroll();
    const mode = window.webvista?.getRtlScrollMode
      ? window.webvista.getRtlScrollMode()
      : "default";
    if (mode === "negative") {
      return max + this.track.scrollLeft;
    }
    if (mode === "reverse") {
      return max - this.track.scrollLeft;
    }
    return this.track.scrollLeft;
  }

  setScrollLeft(value, behavior = "auto") {
    if (!this.track) return;
    const max = this.getMaxScroll();
    const clamped = Math.max(0, Math.min(max, value));
    let target = clamped;
    if (this.getIsRtl()) {
      const mode = window.webvista?.getRtlScrollMode
        ? window.webvista.getRtlScrollMode()
        : "default";
      if (mode === "negative") {
        target = clamped - max;
      } else if (mode === "reverse") {
        target = max - clamped;
      }
    }
    if (behavior === "smooth" && this.track.scrollTo) {
      this.track.scrollTo({ left: target, behavior });
      return;
    }
    this.track.scrollLeft = target;
  }

  onSliderKeyDown(event) {
    if (!this.isEnabled) return;
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    // Skip if focus is inside an input, textarea, or [contenteditable]
    var tag = document.activeElement && document.activeElement.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || (document.activeElement && document.activeElement.isContentEditable)) return;
    event.preventDefault();
    if (event.key === "ArrowLeft") {
      this.onPrevClick();
    } else {
      this.onNextClick();
    }
  }

  onPrevClick() {
    if (!this.isEnabled) return;
    if (!this.track) return;
    if (this.getIsRtl()) {
      const current = this.getActiveIndex();
      this.scrollToIndex(Math.max(0, current - 1));
      return;
    }
    const amount = this.track.clientWidth * 0.8;
    this.setScrollLeft(this.getScrollLeft() - amount, "smooth");
  }

  onNextClick() {
    if (!this.isEnabled) return;
    if (!this.track) return;
    if (this.getIsRtl()) {
      const slides = this.getSlides();
      const current = this.getActiveIndex();
      this.scrollToIndex(Math.min(slides.length - 1, current + 1));
      return;
    }
    const amount = this.track.clientWidth * 0.8;
    this.setScrollLeft(this.getScrollLeft() + amount, "smooth");
  }

  setupAutoplay() {
    if (!this.track) return;
    const autoplayAttr = this.getAttribute("data-slider-autoplay");
    this.autoplayEnabled = autoplayAttr !== null && autoplayAttr !== "false";
    if (!this.autoplayEnabled) return;
    const intervalAttr = Number.parseInt(
      this.getAttribute("data-slider-interval"),
      10,
    );
    if (!Number.isNaN(intervalAttr) && intervalAttr > 0) {
      this.autoplayInterval = intervalAttr;
    }
    const pauseAttr = this.getAttribute("data-slider-pause-on-hover");
    this.pauseOnHover = pauseAttr !== "false";
    if (window.matchMedia) {
      this.prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      );
      if (this.prefersReducedMotion.matches) return;
    }
    this.addAutoplayListeners();
    this.startAutoplay();
  }

  addAutoplayListeners() {
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    if (this.pauseOnHover) {
      this.addEventListener("mouseenter", this.onMouseEnter);
      this.addEventListener("mouseleave", this.onMouseLeave);
    }
    this.addEventListener("focusin", this.onFocusIn);
    this.addEventListener("focusout", this.onFocusOut);
  }

  removeAutoplayListeners() {
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    if (this.pauseOnHover) {
      this.removeEventListener("mouseenter", this.onMouseEnter);
      this.removeEventListener("mouseleave", this.onMouseLeave);
    }
    this.removeEventListener("focusin", this.onFocusIn);
    this.removeEventListener("focusout", this.onFocusOut);
  }

  onVisibilityChange() {
    if (document.hidden) {
      this.pauseAutoplay();
    } else {
      this.resumeAutoplay();
    }
  }

  onMouseEnter() {
    this.pauseAutoplay();
  }

  onMouseLeave() {
    this.resumeAutoplay();
  }

  onFocusIn() {
    this.pauseAutoplay();
  }

  onFocusOut() {
    this.resumeAutoplay();
  }

  startAutoplay() {
    if (!this.autoplayEnabled || this.autoplayTimer) return;
    const slides = this.getSlides();
    if (slides.length < 2) return;
    this.autoplayTimer = setInterval(() => {
      if (this.autoplayPaused || document.hidden) return;
      this.advanceSlide();
    }, this.autoplayInterval);
  }

  stopAutoplay() {
    if (!this.autoplayTimer) return;
    clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  pauseAutoplay() {
    if (!this.autoplayEnabled) return;
    this.autoplayPaused = true;
  }

  resumeAutoplay() {
    if (!this.autoplayEnabled) return;
    this.autoplayPaused = false;
    this.startAutoplay();
  }

  getSlides() {
    if (!this.track) return [];
    return Array.from(this.track.children);
  }

  bindEditorEvents() {
    if (!window.Shopify || !window.Shopify.designMode) {
      return;
    }

    if (
      !window.webvista ||
      typeof window.webvista.bindShopifyEditorBlockEvents !== "function"
    ) {
      return;
    }

    if (this.editorCleanup) {
      this.editorCleanup();
      this.editorCleanup = null;
    }

    this.editorCleanup = window.webvista.bindShopifyEditorBlockEvents({
      root: this,
      onSelect: ({ blockElement }) => {
        const slide = this.getSlideFromBlockElement(blockElement);
        if (!slide) return;
        const slides = this.getSlides();
        const index = slides.indexOf(slide);
        if (index < 0) return;
        this.scrollToIndex(index);
      },
    });
  }

  getSlideFromBlockElement(blockElement) {
    if (!(blockElement instanceof Element) || !this.track) return null;
    if (!this.track.contains(blockElement)) return null;

    let current = blockElement;
    while (
      current &&
      current.parentElement &&
      current.parentElement !== this.track
    ) {
      current = current.parentElement;
    }

    if (!current || current.parentElement !== this.track) return null;
    return current;
  }

  getSlideLeft(slide) {
    if (!this.track || !slide) return 0;
    const trackRect = this.track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    return slideRect.left - trackRect.left + this.getScrollLeft();
  }

  getActiveIndex() {
    if (!this.track) return 0;
    const slides = this.getSlides();
    if (!slides.length) return 0;
    if (!this.getIsRtl()) {
      const scrollLeft = this.getScrollLeft();
      let activeIndex = 0;
      let smallest = Infinity;
      slides.forEach((slide, index) => {
        const target = this.getSlideLeft(slide);
        const distance = Math.abs(target - scrollLeft);
        if (distance < smallest) {
          smallest = distance;
          activeIndex = index;
        }
      });
      return activeIndex;
    }
    const trackRect = this.track.getBoundingClientRect();
    let activeIndex = 0;
    let smallest = Infinity;
    slides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      // In RTL, "inline-start" is the right edge.
      const distance = Math.abs(rect.right - trackRect.right);
      if (distance < smallest) {
        smallest = distance;
        activeIndex = index;
      }
    });
    return activeIndex;
  }

  scrollToIndex(index) {
    if (!this.track) return;
    const slides = this.getSlides();
    const target = slides[index];
    if (!target) return;
    if (this.getIsRtl()) {
      target.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
      return;
    }
    const maxScroll = this.getMaxScroll();
    const scrollTarget = this.getSlideLeft(target);
    const clamped = Math.max(0, Math.min(maxScroll, scrollTarget));
    this.setScrollLeft(clamped, "smooth");
  }

  advanceSlide() {
    const slides = this.getSlides();
    if (slides.length < 2) return;
    const current = this.getActiveIndex();
    const nextIndex = current + 1 >= slides.length ? 0 : current + 1;
    this.scrollToIndex(nextIndex);
  }

  onDotClick(event) {
    if (!this.track) return;
    const dot = event.target.closest("[data-slider-dot]");
    if (!dot || !this.dots || !this.dots.contains(dot)) return;
    const index = Number.parseInt(dot.getAttribute("data-slider-dot"), 10);
    if (Number.isNaN(index)) return;
    const slides = Array.from(this.track.children);
    const target = slides[index];
    if (!target) return;
    if (this.getIsRtl()) {
      target.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
      return;
    }
    const maxScroll = this.getMaxScroll();
    const clamped = Math.max(0, Math.min(maxScroll, target.offsetLeft));
    this.setScrollLeft(clamped, "smooth");
  }

  buildDots() {
    if (!this.dots || !this.track) return;
    const slides = Array.from(this.track.children);
    if (slides.length < 2) {
      this.dots.innerHTML = "";
      this.dots.style.display = "none";
      return;
    }
    const existing = this.dots.querySelectorAll("[data-slider-dot]");
    if (existing.length === slides.length) return;
    this.dots.innerHTML = "";
    this.dots.style.display = "";
    const baseClasses = this.dots.getAttribute("data-slider-dot-classes") || "";
    const baseOpacityAttr = this.dots.getAttribute("data-slider-dot-opacity");
    const baseOpacity =
      baseOpacityAttr !== null ? Number.parseFloat(baseOpacityAttr) : 0.3;
    slides.forEach((slide, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.setAttribute("data-slider-dot", index);
      button.setAttribute("aria-label", `Go to slide ${index + 1}`);
      if (baseClasses) {
        button.className = baseClasses;
      }
      if (!Number.isNaN(baseOpacity)) {
        button.style.opacity = String(baseOpacity);
      }
      this.dots.appendChild(button);
    });
  }

  updateDots() {
    if (!this.dots || !this.track) return;
    const dots = Array.from(this.dots.querySelectorAll("[data-slider-dot]"));
    if (!dots.length) return;
    const slides = Array.from(this.track.children);
    if (slides.length < 2) {
      this.dots.innerHTML = "";
      this.dots.style.display = "none";
      return;
    }
    this.dots.style.display = "";
    const activeIndex = this.getActiveIndex();
    const activeClasses =
      this.dots.getAttribute("data-slider-dot-active-classes") || "";
    const activeClassList = activeClasses
      .split(" ")
      .map((value) => value.trim())
      .filter(Boolean);
    const baseOpacityAttr = this.dots.getAttribute("data-slider-dot-opacity");
    const activeOpacityAttr = this.dots.getAttribute(
      "data-slider-dot-active-opacity",
    );
    const baseOpacity =
      baseOpacityAttr !== null ? Number.parseFloat(baseOpacityAttr) : 0.3;
    const activeOpacity =
      activeOpacityAttr !== null ? Number.parseFloat(activeOpacityAttr) : 1;
    dots.forEach((dot, index) => {
      const isActive = index === activeIndex;
      dot.setAttribute("aria-current", isActive ? "true" : "false");
      if (activeClassList.length) {
        activeClassList.forEach((className) => {
          dot.classList.toggle(className, isActive);
        });
      }
      if (!Number.isNaN(baseOpacity) && !Number.isNaN(activeOpacity)) {
        dot.style.opacity = isActive
          ? String(activeOpacity)
          : String(baseOpacity);
      }
    });
  }

  updateScrollbar() {
    if (!this.scrollbar || !this.scrollbarThumb || !this.track) return;
    if (!this.isEnabled) {
      this.scrollbar.setAttribute("aria-hidden", "true");
      this.scrollbar.style.display = "none";
      return;
    }
    const maxScroll = this.getMaxScroll();
    if (maxScroll <= 0) {
      this.scrollbar.setAttribute("aria-hidden", "true");
      this.scrollbar.style.display = "none";
      return;
    }
    this.scrollbar.setAttribute("aria-hidden", "false");
    this.scrollbar.style.display = "";
    const widthPercent = Math.min(
      100,
      (this.track.clientWidth / this.track.scrollWidth) * 100,
    );
    const leftPercent =
      (this.getScrollLeft() / maxScroll) * (100 - widthPercent);
    this.scrollbarThumb.style.width = `${widthPercent}%`;
    this.scrollbarThumb.style.left = `${leftPercent}%`;
  }

  onScrollbarPointerDown(event) {
    if (!this.track || !this.scrollbar || !this.scrollbarThumb) return;
    if (!this.isEnabled || event.button !== 0) return;
    const thumb = event.target.closest("[data-slider-scrollbar-thumb]");
    if (!thumb) {
      this.jumpToScrollbarPosition(event);
      return;
    }
    event.preventDefault();
    this.scrollbarPrevSnapType = this.track.style.scrollSnapType;
    this.scrollbarPrevScrollBehavior = this.track.style.scrollBehavior;
    this.track.style.scrollSnapType = "none";
    this.track.style.scrollBehavior = "auto";
    this.isScrollbarDragging = true;
    this.scrollbarPointerId = event.pointerId;
    this.scrollbarDragStartX = event.clientX;
    this.scrollbarDragStartScroll = this.getScrollLeft();
    this.scrollbarThumb.classList.add("cursor-grabbing");
    this.scrollbarThumb.classList.remove("cursor-grab");
    try {
      this.scrollbarThumb.setPointerCapture(event.pointerId);
    } catch (error) {}
    this.addScrollbarDragListeners();
  }

  onScrollbarPointerMove(event) {
    if (!this.isScrollbarDragging) return;
    if (!this.track || !this.scrollbar || !this.scrollbarThumb) return;
    const maxScroll = this.getMaxScroll();
    if (maxScroll <= 0) return;
    const trackWidth =
      this.scrollbar.clientWidth - this.scrollbarThumb.offsetWidth;
    if (trackWidth <= 0) return;
    const deltaX = event.clientX - this.scrollbarDragStartX;
    const nextScroll =
      this.scrollbarDragStartScroll + (deltaX / trackWidth) * maxScroll;
    this.scrollbarTargetScroll = Math.max(0, Math.min(maxScroll, nextScroll));
    if (this.scrollbarRaf) return;
    this.scrollbarRaf = requestAnimationFrame(() => {
      this.scrollbarRaf = null;
      if (!this.track || this.scrollbarTargetScroll === null) return;
      this.setScrollLeft(this.scrollbarTargetScroll);
    });
  }

  onScrollbarPointerUp(event) {
    if (!this.isScrollbarDragging) return;
    this.isScrollbarDragging = false;
    if (this.scrollbarRaf) {
      cancelAnimationFrame(this.scrollbarRaf);
      this.scrollbarRaf = null;
    }
    if (this.scrollbarTargetScroll !== null && this.track) {
      this.setScrollLeft(this.scrollbarTargetScroll);
    }
    this.scrollbarTargetScroll = null;
    if (this.scrollbarThumb && this.scrollbarPointerId !== null) {
      try {
        this.scrollbarThumb.releasePointerCapture(this.scrollbarPointerId);
      } catch (error) {}
    }
    this.scrollbarPointerId = null;
    if (this.scrollbarThumb) {
      this.scrollbarThumb.classList.remove("cursor-grabbing");
      this.scrollbarThumb.classList.add("cursor-grab");
    }
    if (this.track) {
      this.track.style.scrollSnapType = this.scrollbarPrevSnapType;
      this.track.style.scrollBehavior = this.scrollbarPrevScrollBehavior;
      this.snapToNearest();
    }
    this.removeScrollbarDragListeners();
  }

  addScrollbarDragListeners() {
    document.addEventListener("pointermove", this.onScrollbarPointerMove);
    document.addEventListener("pointerup", this.onScrollbarPointerUp);
    document.addEventListener("pointercancel", this.onScrollbarPointerUp);
  }

  removeScrollbarDragListeners() {
    document.removeEventListener("pointermove", this.onScrollbarPointerMove);
    document.removeEventListener("pointerup", this.onScrollbarPointerUp);
    document.removeEventListener("pointercancel", this.onScrollbarPointerUp);
  }

  jumpToScrollbarPosition(event) {
    if (!this.track || !this.scrollbar || !this.scrollbarThumb) return;
    const maxScroll = this.getMaxScroll();
    if (maxScroll <= 0) return;
    const rect = this.scrollbar.getBoundingClientRect();
    const thumbWidth = this.scrollbarThumb.offsetWidth;
    const trackWidth = rect.width - thumbWidth;
    if (trackWidth <= 0) return;
    const clickX = event.clientX - rect.left - thumbWidth / 2;
    const ratio = Math.max(0, Math.min(1, clickX / trackWidth));
    this.setScrollLeft(ratio * maxScroll, "smooth");
  }

  onPointerDown(event) {
    if (!this.isEnabled) return;
    if (!this.track) return;
    // Only allow mouse pointers to drag. Touch/pen use native scroll-snap.
    // Note: don't rely on navigator.maxTouchPoints — Windows laptops with
    // precision touchpads report it > 0 even though they have no touchscreen,
    // which would disable mouse dragging entirely.
    if (event.pointerType && event.pointerType !== "mouse") return;
    if (event.button !== 0) return;
    if (
      event.target &&
      event.target.closest(
        "button, input, textarea, select, model-viewer, [data-slider-no-drag]",
      )
    ) {
      return;
    }
    if (this.snapRestoreTimer) {
      clearTimeout(this.snapRestoreTimer);
      this.snapRestoreTimer = null;
      this.restoreSnapStyles();
    }
    if (this.resetTransformTimer) {
      clearTimeout(this.resetTransformTimer);
      this.resetTransformTimer = null;
    }
    this.isDragging = true;
    this.dragHasMoved = false;
    this.dragPointerId = event.pointerId;
    this.dragStartX = event.clientX;
    // Prevent text selection on mousedown (before threshold is reached)
    this.track.classList.add("select-none");
    this.dragStartScroll = this.getScrollLeft();
    this.track.addEventListener("pointermove", this.onPointerMove);
    this.track.addEventListener("pointerup", this.onPointerUp);
    this.track.addEventListener("pointercancel", this.onPointerUp);
  }

  startDrag() {
    if (!this.track || this.dragHasMoved) return;
    this.dragHasMoved = true;
    if (this.dragPointerId !== null) {
      try {
        this.track.setPointerCapture(this.dragPointerId);
      } catch (error) {}
    }
    this.track.style.transition = "";
    this.track.classList.add("select-none", "cursor-grabbing");
    this.track.classList.remove("cursor-grab");
    this.prevScrollSnapType = this.track.style.scrollSnapType;
    this.prevScrollBehavior = this.track.style.scrollBehavior;
    this.track.style.scrollSnapType = "none";
    this.track.style.scrollBehavior = "auto";
  }

  onPointerMove(event) {
    if (!this.track || !this.isDragging) return;
    const rawDelta = event.clientX - this.dragStartX;
    if (!this.dragHasMoved && Math.abs(rawDelta) < this.dragThreshold) {
      return;
    }
    event.preventDefault();
    this.startDrag();
    const delta = rawDelta * this.dragSpeed;
    const maxScroll = this.getMaxScroll();
    let nextScroll = this.dragStartScroll - delta;
    if (nextScroll < 0) {
      const elastic = Math.min(60, Math.abs(nextScroll) * 0.3);
      this.track.style.transform = `translateX(${elastic}px)`;
      nextScroll = 0;
    } else if (nextScroll > maxScroll) {
      const over = nextScroll - maxScroll;
      const elastic = Math.min(60, Math.abs(over) * 0.3);
      this.track.style.transform = `translateX(-${elastic}px)`;
      nextScroll = maxScroll;
    } else {
      this.track.style.transform = "";
    }
    this.dragTargetScroll = nextScroll;
    if (this.dragRaf) return;
    this.dragRaf = requestAnimationFrame(() => {
      this.dragRaf = null;
      if (!this.track || this.dragTargetScroll === null) return;
      this.setScrollLeft(this.dragTargetScroll);
    });
  }

  onDragStart(event) {
    event.preventDefault();
  }

  onPointerUp(event) {
    if (!this.track || !this.isDragging) return;
    if (this.dragPointerId !== null) {
      try {
        this.track.releasePointerCapture(this.dragPointerId);
      } catch (error) {}
    }
    const wasDragging = this.dragHasMoved;
    this.isDragging = false;
    this.dragHasMoved = false;
    this.dragPointerId = null;
    this.track.classList.remove("select-none", "cursor-grabbing");
    this.track.classList.add("cursor-grab");
    if (this.dragRaf) {
      cancelAnimationFrame(this.dragRaf);
      this.dragRaf = null;
    }
    if (this.dragTargetScroll !== null) {
      this.setScrollLeft(this.dragTargetScroll);
    }
    this.dragTargetScroll = null;
    if (!wasDragging) {
      this.restoreSnapStyles();
      this.removeDragListeners();
      return;
    }
    this.dragDelta = this.getScrollLeft() - this.dragStartScroll;
    const maxScroll = this.getMaxScroll();
    const edgeThreshold = Math.max(24, this.track.clientWidth * 0.06);
    if (maxScroll > 0) {
      if (this.getScrollLeft() >= maxScroll - edgeThreshold) {
        this.edgeSnapTarget = maxScroll;
      } else if (this.getScrollLeft() <= edgeThreshold) {
        this.edgeSnapTarget = 0;
      } else {
        this.edgeSnapTarget = null;
      }
    } else {
      this.edgeSnapTarget = null;
    }
    if (this.track.style.transform) {
      this.track.style.transition = "transform 220ms ease-out";
      this.track.style.transform = "";
      this.resetTransformTimer = setTimeout(() => {
        if (!this.track) return;
        this.track.style.transition = "";
        this.resetTransformTimer = null;
      }, 240);
    }
    this.removeDragListeners();
    const shouldDelaySnap = Boolean(this.track.style.transition);
    const snapDelay = shouldDelaySnap ? 240 : 0;
    this.snapRestoreTimer = setTimeout(() => {
      this.snapToNearest();
      this.restoreSnapStyles();
      this.snapRestoreTimer = null;
    }, snapDelay);
  }

  removeDragListeners() {
    if (!this.track) return;
    this.track.removeEventListener("pointermove", this.onPointerMove);
    this.track.removeEventListener("pointerup", this.onPointerUp);
    this.track.removeEventListener("pointercancel", this.onPointerUp);
  }

  restoreSnapStyles() {
    if (!this.track) return;
    this.track.style.scrollSnapType = this.prevScrollSnapType;
    this.track.style.scrollBehavior = this.prevScrollBehavior;
  }

  snapToNearest() {
    if (!this.track) return;
    if (this.edgeSnapTarget !== null) {
      const clamped = Math.max(
        0,
        Math.min(this.getMaxScroll(), this.edgeSnapTarget),
      );
      this.edgeSnapTarget = null;
      this.track.style.scrollBehavior = "smooth";
      this.setScrollLeft(clamped, "smooth");
      return;
    }
    const slides = Array.from(this.track.children);
    if (!slides.length) return;
    if (this.getIsRtl()) {
      const trackRect = this.track.getBoundingClientRect();
      let nearest = slides[0];
      let smallest = Infinity;
      slides.forEach((slide) => {
        const rect = slide.getBoundingClientRect();
        // In RTL, "inline-start" is the right edge.
        const distance = Math.abs(rect.right - trackRect.right);
        if (distance < smallest) {
          smallest = distance;
          nearest = slide;
        }
      });
      this.dragDelta = 0;
      nearest.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
      return;
    }
    const scrollLeft = this.getScrollLeft();
    const maxScroll = this.getMaxScroll();
    const getTarget = (slide, index) => {
      if (index === 0) {
        return 0;
      }
      if (
        index === slides.length - 1 &&
        maxScroll > 0 &&
        this.getSlideLeft(slide) < maxScroll
      ) {
        return maxScroll;
      }
      return this.getSlideLeft(slide);
    };
    let nearest = slides[0];
    let nearestTarget = getTarget(nearest, 0);
    let smallest = Math.abs(nearestTarget - scrollLeft);
    let nearestIndex = 0;
    slides.forEach((slide, index) => {
      const target = getTarget(slide, index);
      const distance = Math.abs(target - scrollLeft);
      if (distance < smallest) {
        smallest = distance;
        nearest = slide;
        nearestTarget = target;
        nearestIndex = index;
      }
    });
    if (this.dragDelta !== 0) {
      // Find the slide we started on before dragging
      let startIndex = 0;
      let startSmallest = Infinity;
      slides.forEach((slide, index) => {
        const target = getTarget(slide, index);
        const distance = Math.abs(target - this.dragStartScroll);
        if (distance < startSmallest) {
          startSmallest = distance;
          startIndex = index;
        }
      });
      // Only apply momentum if the nearest slide is still the starting one
      // (small drag that didn't scroll past the midpoint). If we already
      // passed the next slide naturally, just snap to nearest — no extra push.
      if (nearestIndex === startIndex) {
        const direction = this.dragDelta > 0 ? 1 : -1;
        const nextIndex = startIndex + direction;
        if (nextIndex >= 0 && nextIndex < slides.length) {
          const slideWidth =
            slides[nextIndex].clientWidth || this.track.clientWidth;
          if (Math.abs(this.dragDelta) >= slideWidth * 0.3) {
            nearestTarget = getTarget(slides[nextIndex], nextIndex);
          }
        }
      }
    }
    this.dragDelta = 0;
    this.track.style.scrollBehavior = "smooth";
    const clamped = Math.max(0, Math.min(maxScroll, nearestTarget));
    this.setScrollLeft(clamped, "smooth");
  }

  updateButtons() {
    if (!this.track || !this.prevButton || !this.nextButton) return;
    const slides = this.getSlides();
    if (slides.length < 2) {
      this.prevButton.disabled = true;
      this.nextButton.disabled = true;
      return;
    }
    const maxScroll = this.getMaxScroll();
    if (maxScroll <= 0) {
      this.prevButton.disabled = true;
      this.nextButton.disabled = true;
      return;
    }
    const scrollLeft = this.getScrollLeft();
    const edgeThreshold = Math.max(1, Math.round(this.track.clientWidth * 0.01));
    this.prevButton.disabled = scrollLeft <= edgeThreshold;
    this.nextButton.disabled = scrollLeft >= maxScroll - edgeThreshold;
  }
}
customElements.define("slider-component", SliderComponent);

// Responsive slider component
// Extends SliderComponent to enable/disable based on viewport size
// Uses data attributes to configure behavior
class ResponsiveSliderComponent extends SliderComponent {
  constructor() {
    super();
    this.mediaQuery = null;
    this.isEnabled = null;
    this.onMediaChange = this.onMediaChange.bind(this);
  }

  connectedCallback() {
    this.track = this.querySelector("[data-slider-track]");
    if (!this.track) return;
    this.mediaQuery = window.matchMedia("(min-width: 768px)");
    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener("change", this.onMediaChange);
    } else if (this.mediaQuery.addListener) {
      this.mediaQuery.addListener(this.onMediaChange);
    }
    this.applyMode();
  }

  disconnectedCallback() {
    if (this.mediaQuery) {
      if (this.mediaQuery.removeEventListener) {
        this.mediaQuery.removeEventListener("change", this.onMediaChange);
      } else if (this.mediaQuery.removeListener) {
        this.mediaQuery.removeListener(this.onMediaChange);
      }
      this.mediaQuery = null;
    }
    if (this.isEnabled) {
      super.disconnectedCallback();
    }
    this.isEnabled = null;
  }

  onMediaChange() {
    this.applyMode();
  }

  applyMode() {
    if (!this.track) return;
    const enableMobile = this.getAttribute("data-enable-mobile") === "true";
    const enableDesktop = this.getAttribute("data-enable-desktop") === "true";
    const isDesktop = this.mediaQuery
      ? this.mediaQuery.matches
      : window.innerWidth >= 768;
    const shouldEnable = isDesktop ? enableDesktop : enableMobile;
    if (shouldEnable === this.isEnabled) return;

    if (this.isEnabled) {
      super.disconnectedCallback();
    }

    this.isEnabled = shouldEnable;
    this.updateTrackClasses(shouldEnable);
    this.updateItemClasses(shouldEnable);

    if (shouldEnable) {
      super.connectedCallback();
    } else {
      this.track.style.transform = "";
      this.track.style.transition = "";
      this.track.style.scrollSnapType = "";
      this.track.style.scrollBehavior = "";
      this.track.classList.remove(
        "cursor-grab",
        "cursor-grabbing",
        "select-none",
      );
    }
  }

  updateTrackClasses(enabled) {
    this.toggleClasses(
      this.track,
      this.getAttribute("data-slider-enabled-classes"),
      this.getAttribute("data-slider-disabled-classes"),
      enabled,
    );
  }

  updateItemClasses(enabled) {
    const items = this.querySelectorAll("[data-slider-item]");
    items.forEach((item) => {
      this.toggleClasses(
        item,
        item.getAttribute("data-slider-item-enabled-classes"),
        item.getAttribute("data-slider-item-disabled-classes"),
        enabled,
      );
    });
  }

  toggleClasses(element, enabledClasses, disabledClasses, enabled) {
    if (!element) return;
    const add = enabled ? enabledClasses : disabledClasses;
    const remove = enabled ? disabledClasses : enabledClasses;
    this.parseClassList(remove).forEach((name) =>
      element.classList.remove(name),
    );
    this.parseClassList(add).forEach((name) => element.classList.add(name));
  }

  parseClassList(value) {
    if (!value) return [];
    return value.split(/\s+/).filter(Boolean);
  }
}
customElements.define("responsive-slider-component", ResponsiveSliderComponent);

// Quantity Input Component
// Handles incrementing and decrementing quantity input values
class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = null;
    this.onClick = this.onClick.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.changeEvent = new Event("change", { bubbles: true });
  }

  connectedCallback() {
    this.input =
      this.querySelector('input[name="quantity"]') ||
      this.querySelector('input[name="updates[]"]');
    if (!this.input) return;
    this.addEventListener("click", this.onClick);
    this.input.addEventListener("change", this.onInputChange);
    this.updateQtyButtonState();
    const hasBoundaryData =
      this.input.dataset.min ||
      this.input.dataset.max ||
      this.input.dataset.cartQuantity;
    if (hasBoundaryData && this.input.name === "quantity") {
      this.setQuantityBoundaries();
    }
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
    if (this.input) {
      this.input.removeEventListener("change", this.onInputChange);
    }
  }

  onClick(event) {
    const button = event.target.closest("button");
    if (!button || !this.input) return;
    if (button.name === "minus") {
      this.input.stepDown();
    }
    if (button.name === "plus") {
      this.input.stepUp();
    }
    this.input.dispatchEvent(this.changeEvent);
    this.updateQtyButtonState();
  }

  onInputChange() {
    this.updateQtyButtonState();
  }

  // Update quantity button state
  updateQtyButtonState() {
    if (!this.input) return;
    const value = parseInt(this.input.value, 10);
    const buttonMinus = this.querySelector('button[name="minus"]');
    const buttonPlus = this.querySelector('button[name="plus"]');

    if (this.input.min) {
      const min = parseInt(this.input.min, 10);
      if (buttonMinus) {
        const isDisabled = value <= min;
        buttonMinus.classList.toggle("disabled", isDisabled);
        buttonMinus.toggleAttribute("disabled", isDisabled);
      }
    }

    if (this.input.max) {
      const max = parseInt(this.input.max, 10);
      if (buttonPlus) {
        const isDisabled = value >= max;
        buttonPlus.classList.toggle("disabled", isDisabled);
        buttonPlus.toggleAttribute("disabled", isDisabled);
      }
    }
  }

  // Set quantity boundaries based on data attributes and cart quantity
  setQuantityBoundaries(preserveValue = false) {
    if (!this.input) return;
    const data = {
      cartQuantity: this.input.dataset.cartQuantity
        ? parseInt(this.input.dataset.cartQuantity, 10)
        : 0,
      min: this.input.dataset.min
        ? parseInt(this.input.dataset.min, 10)
        : this.input.min
          ? parseInt(this.input.min, 10)
          : 1,
      max: this.input.dataset.max
        ? parseInt(this.input.dataset.max, 10)
        : this.input.max
          ? parseInt(this.input.max, 10)
          : null,
      step: this.input.step ? parseInt(this.input.step, 10) : 1,
    };

    const resolvedMin = Number.isNaN(data.min) ? 1 : data.min;
    const resolvedMax =
      data.max === null || Number.isNaN(data.max) ? null : data.max;
    const resolvedStep = Number.isNaN(data.step) ? 1 : data.step;
    const cartQty = Number.isNaN(data.cartQuantity) ? 0 : data.cartQuantity;

    let min = resolvedMin;
    const max = resolvedMax === null ? resolvedMax : resolvedMax - cartQty;
    if (max !== null) {
      min = Math.min(min, max);
    }
    if (cartQty >= resolvedMin) min = Math.min(min, resolvedStep);

    if (max !== null && max < 0) {
      this.input.min = 0;
      this.input.max = 0;
      this.input.value = 0;
      this.input.dispatchEvent(this.changeEvent);
      this.updateQtyButtonState();
      return;
    }

    this.input.min = min;
    this.input.max = max;
    if (preserveValue) {
      let nextValue = parseInt(this.input.value, 10);
      if (Number.isNaN(nextValue)) {
        nextValue = min;
      }
      if (max !== null) {
        nextValue = Math.min(nextValue, max);
      }
      if (min !== null) {
        nextValue = Math.max(nextValue, min);
      }
      this.input.value = nextValue;
    } else {
      this.input.value = min;
    }
    this.input.dispatchEvent(this.changeEvent);
    this.updateQtyButtonState();
  }
}
customElements.define("quantity-input", QuantityInput);

// Accordion Details Component
class AccordionDetails extends HTMLElement {
  constructor() {
    super();
    this.items = [];
    this.initialOpenItems = new Set();
    this.editorActiveBlockId = null;
    this.editorCleanup = null;
    this.onTriggerClick = this.onTriggerClick.bind(this);
  }

  connectedCallback() {
    this.items = Array.from(this.querySelectorAll("[data-accordion-item]"));
    this.initialOpenItems = new Set(
      this.items.filter((item) => item.hasAttribute("data-open")),
    );
    this.items.forEach((item) => {
      const trigger = item.querySelector("[data-accordion-trigger]");
      if (!trigger) return;
      trigger.addEventListener("click", this.onTriggerClick);
      if (!item.hasAttribute("data-open")) {
        const content = item.querySelector("[data-accordion-content]");
        const inner = content?.firstElementChild;
        if (inner) inner.setAttribute("inert", "");
      }
    });
    this.bindEditorEvents();
  }

  disconnectedCallback() {
    this.editorActiveBlockId = null;
    if (this.editorCleanup) {
      this.editorCleanup();
      this.editorCleanup = null;
    }
    this.items.forEach((item) => {
      const trigger = item.querySelector("[data-accordion-trigger]");
      if (!trigger) return;
      trigger.removeEventListener("click", this.onTriggerClick);
    });
  }

  getAllowMultiple() {
    return (
      this.dataset.accordionMode === "multi" ||
      this.hasAttribute("data-accordion-multi")
    );
  }

  getGroupItems() {
    const allowMultiple =
      this.dataset.accordionMode === "multi" ||
      this.hasAttribute("data-accordion-multi");
    const group = this.dataset.group;
    return group && !allowMultiple
      ? Array.from(
          document.querySelectorAll(
            `accordion-details[data-group="${group}"] [data-accordion-item]`,
          ),
        )
      : this.items;
  }

  setItemOpen(item, isOpen) {
    if (!item) return;
    const trigger = item.querySelector("[data-accordion-trigger]");
    const content = item.querySelector("[data-accordion-content]");
    const inner = content?.firstElementChild;
    const plus = item.querySelector(".product-accordion__plus");

    if (isOpen) {
      item.setAttribute("data-open", "true");
      if (content) {
        content.classList.remove("grid-rows-[0fr]");
        content.classList.add("grid-rows-[1fr]", "pb-4");
      }
      if (inner) inner.removeAttribute("inert");
      if (trigger) {
        trigger.setAttribute("aria-expanded", "true");
      }
      if (plus) plus.classList.remove("rotate-90");
      return;
    }

    item.removeAttribute("data-open");
    if (content) {
      content.classList.remove("grid-rows-[1fr]", "pb-4");
      content.classList.add("grid-rows-[0fr]");
    }
    if (inner) inner.setAttribute("inert", "");
    if (trigger) {
      trigger.setAttribute("aria-expanded", "false");
    }
    if (plus) plus.classList.add("rotate-90");
  }

  closeSiblingItems(activeItem) {
    if (this.getAllowMultiple()) {
      return;
    }
    this.getGroupItems().forEach((item) => {
      if (item === activeItem) {
        return;
      }
      this.setItemOpen(item, false);
    });
  }

  openItem(item) {
    if (!item) {
      return;
    }
    this.closeSiblingItems(item);
    this.setItemOpen(item, true);
  }

  restoreInitialState() {
    this.items.forEach((item) => {
      const shouldOpen = this.initialOpenItems.has(item);
      this.setItemOpen(item, shouldOpen);
    });
  }

  bindEditorEvents() {
    if (
      !window.webvista ||
      typeof window.webvista.bindShopifyEditorBlockEvents !== "function"
    ) {
      return;
    }

    this.editorCleanup = window.webvista.bindShopifyEditorBlockEvents({
      root: this,
      onSelect: ({ blockId, blockElement }) => {
        this.editorActiveBlockId = blockId;
        const targetItem =
          (blockElement &&
            (blockElement.matches("[data-accordion-item]")
              ? blockElement
              : blockElement.querySelector("[data-accordion-item]"))) ||
          this.items[0] ||
          this.querySelector("[data-accordion-item]");
        if (!targetItem) {
          return;
        }
        this.openItem(targetItem);
      },
      onDeselect: ({ blockId }) => {
        if (!blockId || blockId !== this.editorActiveBlockId) {
          return;
        }
        this.editorActiveBlockId = null;
        window.setTimeout(() => {
          if (this.editorActiveBlockId !== null) {
            return;
          }
          this.restoreInitialState();
        }, 0);
      },
    });
  }

  onTriggerClick(event) {
    const trigger = event.currentTarget;
    const item = trigger.closest("[data-accordion-item]");
    if (!item) return;
    const isOpen = item.hasAttribute("data-open");

    if (isOpen) {
      this.setItemOpen(item, false);
      return;
    }

    this.openItem(item);
  }
}
customElements.define("accordion-details", AccordionDetails);

// Tabs Base Component
class TabsBase extends HTMLElement {
  constructor() {
    super();
    this.buttons = [];
    this.panels = [];
    this.track = null;
    this.nav = null;
    this.activeId = null;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchDeltaX = 0;
    this.touchDeltaY = 0;
    this.onTabClick = this.onTabClick.bind(this);
    this.onTabKeydown = this.onTabKeydown.bind(this);
    this.onPanelKeydown = this.onPanelKeydown.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
  }

  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll("[data-tab-button]"));
    this.panels = Array.from(this.querySelectorAll("[data-tab-panel]"));
    this.track = this.querySelector("[data-tabs-track]");
    this.nav = this.querySelector("[data-tabs-nav]");

    if (!this.buttons.length || !this.panels.length) return;

    this.buttons.forEach((button) => {
      button.addEventListener("click", this.onTabClick);
      button.addEventListener("keydown", this.onTabKeydown);
    });

    this.panels.forEach((panel) => {
      panel.addEventListener("keydown", this.onPanelKeydown);
    });

    // Swipe-to-switch is opt-in. Add `data-tabs-swipe="true"` to enable.
    if (this.dataset.tabsSwipe === "true") {
      this.bindSwipe();
    }

    const selected = this.buttons.find(
      (button) => button.getAttribute("aria-selected") === "true",
    );
    const firstId = this.buttons[0]
      ? this.buttons[0].getAttribute("data-tab-id")
      : null;
    const initialId =
      (selected && selected.getAttribute("data-tab-id")) || firstId;
    this.setActiveTab(initialId, { scrollToNav: false });

    // Shopify editor: activate tab when block is selected
    this._onEditorBlockSelect = (event) => {
      const blockId = event.detail && event.detail.blockId;
      if (!blockId) return;
      // Find button whose data-tab-id ends with the block id
      const button = this.buttons.find((b) => {
        const tabId = b.getAttribute("data-tab-id") || "";
        return tabId.endsWith(blockId);
      });
      if (button) {
        this.setActiveTab(button.getAttribute("data-tab-id"));
      }
    };
    document.addEventListener("shopify:block:select", this._onEditorBlockSelect);
  }

  disconnectedCallback() {
    this.buttons.forEach((button) => {
      button.removeEventListener("click", this.onTabClick);
      button.removeEventListener("keydown", this.onTabKeydown);
    });

    this.panels.forEach((panel) => {
      panel.removeEventListener("keydown", this.onPanelKeydown);
    });

    if (this.track) {
      this.track.removeEventListener("touchstart", this.onTouchStart);
      this.track.removeEventListener("touchmove", this.onTouchMove);
      this.track.removeEventListener("touchend", this.onTouchEnd);
    }

    if (this._onEditorBlockSelect) {
      document.removeEventListener("shopify:block:select", this._onEditorBlockSelect);
      this._onEditorBlockSelect = null;
    }
  }

  onTabClick(event) {
    const button = event.currentTarget;
    const targetId = button.getAttribute("data-tab-id");
    this.setActiveTab(targetId);
  }

  // Keyboard navigation for tabs (WAI-ARIA Tabs pattern)
  onTabKeydown(event) {
    var key = event.key;
    if (
      key !== "ArrowLeft" &&
      key !== "ArrowRight" &&
      key !== "Home" &&
      key !== "End"
    )
      return;

    event.preventDefault();

    var currentIndex = this.buttons.indexOf(event.currentTarget);
    var nextIndex;

    if (key === "ArrowRight") {
      nextIndex = (currentIndex + 1) % this.buttons.length;
    } else if (key === "ArrowLeft") {
      nextIndex =
        (currentIndex - 1 + this.buttons.length) % this.buttons.length;
    } else if (key === "Home") {
      nextIndex = 0;
    } else if (key === "End") {
      nextIndex = this.buttons.length - 1;
    }

    var targetId = this.buttons[nextIndex].getAttribute("data-tab-id");
    this.setActiveTab(targetId);
    this.buttons[nextIndex].focus();
  }

  // Escape anywhere in panel, or Shift+Tab on first element → return to active tab
  onPanelKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      var active = this.buttons.find(
        function (b) { return b.getAttribute("aria-selected") === "true"; }
      );
      if (active) active.focus();
      return;
    }

    if (event.key !== "Tab" || !event.shiftKey) return;

    var panel = event.currentTarget;
    var focusable = webvista.getFocusableElements(panel);
    if (focusable.length && document.activeElement !== focusable[0]) return;

    var activeButton = this.buttons.find(
      function (b) { return b.getAttribute("aria-selected") === "true"; }
    );
    if (!activeButton) return;

    event.preventDefault();
    activeButton.focus();
  }

  bindSwipe() {
    if (!this.track) return;
    this.track.addEventListener("touchstart", this.onTouchStart, {
      passive: true,
    });
    this.track.addEventListener("touchmove", this.onTouchMove, {
      passive: true,
    });
    this.track.addEventListener("touchend", this.onTouchEnd, {
      passive: true,
    });
  }

  onTouchStart(event) {
    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchDeltaX = 0;
    this.touchDeltaY = 0;
  }

  onTouchMove(event) {
    const touch = event.touches[0];
    this.touchDeltaX = touch.clientX - this.touchStartX;
    this.touchDeltaY = touch.clientY - this.touchStartY;
  }

  onTouchEnd() {
    const absX = Math.abs(this.touchDeltaX);
    const absY = Math.abs(this.touchDeltaY);
    if (absX < 40 || absX < absY) return;

    if (this.touchDeltaX < 0) {
      this.setActiveByOffset(1);
    } else {
      this.setActiveByOffset(-1);
    }
  }

  setActiveByOffset(offset) {
    const ids = this.buttons.map((button) =>
      button.getAttribute("data-tab-id"),
    );
    const currentIndex = ids.indexOf(this.activeId);
    if (currentIndex === -1) return;

    const nextIndex = Math.max(
      0,
      Math.min(ids.length - 1, currentIndex + offset),
    );
    if (nextIndex === currentIndex) return;
    this.setActiveTab(ids[nextIndex]);
  }

  setActiveTab(targetId, options = {}) {
    if (!targetId || this.activeId === targetId) return;

    this.activeId = targetId;
    const { scrollToNav = true } = options;

    const activeClass = this.dataset.tabActiveClass || "text-foreground";
    const inactiveClass = this.dataset.tabInactiveClass || "text-foreground/40";
    const inactiveHoverClass =
      this.dataset.tabInactiveHoverClass || "hover:text-foreground/70";
    const indicatorActiveClass =
      this.dataset.tabIndicatorActiveClass || "scale-x-100";
    const indicatorInactiveClass =
      this.dataset.tabIndicatorInactiveClass || "scale-x-0";
    const indicatorSelector =
      this.dataset.tabIndicatorSelector || "[data-tab-indicator]";
    const panelInnerSelector =
      this.dataset.tabPanelInnerSelector || ".routine-tabs__panel-inner";

    this.buttons.forEach((button) => {
      const isActive = button.getAttribute("data-tab-id") === targetId;
      button.setAttribute("aria-selected", String(isActive));
      button.setAttribute("tabindex", isActive ? "0" : "-1");
      button.classList.toggle(activeClass, isActive);
      button.classList.toggle(inactiveClass, !isActive);
      button.classList.toggle(inactiveHoverClass, !isActive);

      const indicator = button.querySelector(indicatorSelector);
      if (indicator) {
        indicator.classList.toggle(indicatorActiveClass, isActive);
        indicator.classList.toggle(indicatorInactiveClass, !isActive);
      }

      if (isActive && this.nav && scrollToNav) {
        button.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    });

    this.panels.forEach((panel) => {
      const isActive = panel.id === targetId;
      panel.classList.toggle("hidden", !isActive);
      panel.setAttribute("aria-hidden", String(!isActive));
      const inner = panel.querySelector(panelInnerSelector);
      if (inner) {
        inner.classList.toggle("animate-fade-in-up", isActive);
      }
    });
  }
}
customElements.define("tabs-base", TabsBase);

// Countdown Timer Component
class CountdownTimer extends HTMLElement {
  connectedCallback() {
    if (this.dataset.countdownInit === "true") return;

    this.dataset.countdownInit = "true";

    const endValue = this.getAttribute("data-countdown-end");
    if (!endValue) return;

    const endTime = new Date(endValue.replace(" ", "T"));
    if (Number.isNaN(endTime.getTime())) return;

    this.endTime = endTime;
    this.daysEl = this.querySelector("[data-countdown-days]");
    this.hoursEl = this.querySelector("[data-countdown-hours]");
    this.minutesEl = this.querySelector("[data-countdown-minutes]");
    this.secondsEl = this.querySelector("[data-countdown-seconds]");

    this.updateCountdown = this.updateCountdown.bind(this);
    this.updateCountdown();
    this.timerId = setInterval(this.updateCountdown, 1000);
  }

  disconnectedCallback() {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  updateCountdown() {
    if (!this.endTime) return;

    const diff = Math.max(0, this.endTime.getTime() - Date.now());
    const hasDays = Boolean(this.daysEl);
    const days = hasDays ? Math.floor(diff / 86400000) : 0;
    const hours = hasDays
      ? Math.floor((diff % 86400000) / 3600000)
      : Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    const daysValue = String(days).padStart(2, "0");
    const hoursValue = String(hours).padStart(2, "0");
    const minutesValue = String(minutes).padStart(2, "0");
    const secondsValue = String(seconds).padStart(2, "0");

    if (this.daysEl && this.daysEl.textContent !== daysValue)
      this.daysEl.textContent = daysValue;
    this.updateDisplay(hoursValue, minutesValue, secondsValue);

    if (diff <= 0 && this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  updateDisplay(hoursValue, minutesValue, secondsValue) {
    if (this.hoursEl && this.hoursEl.textContent !== hoursValue)
      this.hoursEl.textContent = hoursValue;
    if (this.minutesEl && this.minutesEl.textContent !== minutesValue)
      this.minutesEl.textContent = minutesValue;
    if (this.secondsEl && this.secondsEl.textContent !== secondsValue)
      this.secondsEl.textContent = secondsValue;
  }
}
customElements.define("countdown-timer", CountdownTimer);

// Video Autoplay Component
class VideoAutoplay extends HTMLElement {
  connectedCallback() {
    if (this.dataset.playBound === "true") return;

    this.playButton = this.querySelector("[data-play-button]");
    this.video = this.querySelector("video");
    this.playIcon = this.querySelector("[data-icon-play]");
    this.pauseIcon = this.querySelector("[data-icon-pause]");
    this.muteButton = this.querySelector("[data-mute-button]");
    this.muteIcon = this.querySelector("[data-icon-muted]");
    this.unmuteIcon = this.querySelector("[data-icon-unmuted]");
    this.progressBar = null;
    this.progressFill = null;
    this._observer = null;

    if (!this.video) return;
    if (!this.playButton && !this.muteButton) return;

    this.dataset.playBound = "true";
    this.initProgress();
    this.initViewportObserver();
    if (this.playButton) {
      this.playButton.addEventListener("click", () => this.handleToggle());
    }
    if (this.muteButton) {
      this.muteButton.addEventListener("click", () => this.handleMuteToggle());
    }
    this.video.addEventListener("play", () => this.updateUi(true));
    this.video.addEventListener("pause", () => this.updateUi(false));
    this.video.addEventListener("ended", () => this.updateUi(false));
    this.video.addEventListener("timeupdate", () => this.updateProgress());
    this.video.addEventListener("durationchange", () => this.updateProgress());
    this.video.addEventListener("loadedmetadata", () => this.updateProgress());
    this.video.addEventListener("volumechange", () => this.updateMuteUi());

    this.updateUi(!this.video.paused);
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  initViewportObserver() {
    if (!this.video) return;
    this._observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            this.video.muted = true;
            this.updateMuteUi();
          }
        }
      },
      { threshold: 0.25 },
    );
    this._observer.observe(this);
  }

  handleToggle() {
    if (!this.video) return;
    if (this.video.paused) {
      const playPromise = this.video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    } else {
      this.video.pause();
    }
  }

  play() {
    if (!this.video) return;
    const playPromise = this.video.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {});
    }
  }

  pause() {
    if (!this.video) return;
    this.video.pause();
  }

  toggleMute(forceState = null) {
    if (!this.video) return;
    if (typeof forceState === "boolean") {
      this.video.muted = forceState;
    } else {
      this.video.muted = !this.video.muted;
    }
    this.updateMuteUi();
  }

  updateUi(isPlaying) {
    this.classList.toggle("is-playing", isPlaying);
    if (this.playButton) {
      this.playButton.setAttribute(
        "aria-label",
        isPlaying ? "Pause video" : "Play video",
      );
      this.playButton.setAttribute(
        "aria-pressed",
        isPlaying ? "true" : "false",
      );
    }
    if (this.playIcon) {
      this.playIcon.classList.toggle("hidden", isPlaying);
    }
    if (this.pauseIcon) {
      this.pauseIcon.classList.toggle("hidden", !isPlaying);
    }
    if (this.muteButton) {
      this.muteButton.classList.toggle("hidden", !isPlaying);
    }
    if (isPlaying) {
      this.updateMuteUi();
    }
  }

  handleMuteToggle() {
    if (!this.video) return;
    this.video.muted = !this.video.muted;
    this.updateMuteUi();
  }

  updateMuteUi() {
    if (!this.video || !this.muteButton) return;
    const isMuted = this.video.muted;
    this.muteButton.setAttribute("aria-label", isMuted ? "Muted" : "Unmuted");
    this.muteButton.setAttribute("aria-pressed", isMuted ? "true" : "false");
    if (this.muteIcon) {
      this.muteIcon.classList.toggle("hidden", !isMuted);
    }
    if (this.unmuteIcon) {
      this.unmuteIcon.classList.toggle("hidden", isMuted);
    }
  }

  initProgress() {
    if (!this.video) return;
    const shouldShowProgress = this.dataset.showProgress === "true";
    if (!shouldShowProgress) return;
    if (this.querySelector("[data-video-progress]")) {
      this.progressBar = this.querySelector("[data-video-progress]");
      this.progressFill = this.progressBar.querySelector(
        "[data-video-progress-fill]",
      );
      return;
    }
    const pos = getComputedStyle(this).position;
    if (pos === "static") this.style.position = "relative";
    const bar = document.createElement("div");
    bar.setAttribute("data-video-progress", "");
    bar.style.position = "absolute";
    bar.style.left = "0";
    bar.style.right = "0";
    bar.style.bottom = "0";
    const heightValue = Number(this.dataset.progressHeight || 2);
    const height = Number.isNaN(heightValue) ? 2 : Math.max(heightValue, 1);
    bar.style.height = `${height}px`;
    bar.style.color = "rgba(255, 255, 255, 0.8)";
    bar.style.background = "rgba(255, 255, 255, 0.1)";
    bar.style.pointerEvents = "none";
    bar.style.opacity = "0";

    const fill = document.createElement("div");
    fill.setAttribute("data-video-progress-fill", "");
    fill.style.height = "100%";
    fill.style.width = "0%";
    fill.style.background = "currentColor";
    fill.style.transition = "width 0.1s linear";

    bar.appendChild(fill);
    this.appendChild(bar);
    this.progressBar = bar;
    this.progressFill = fill;
  }

  updateProgress() {
    if (!this.video || !this.progressBar || !this.progressFill) return;
    const duration = this.video.duration || 0;
    if (!duration || Number.isNaN(duration)) {
      this.progressBar.style.opacity = "0";
      this.progressFill.style.width = "0%";
      return;
    }
    const current = this.video.currentTime || 0;
    const percent = Math.min(Math.max(current / duration, 0), 1) * 100;
    this.progressBar.style.opacity = "1";
    this.progressFill.style.width = `${percent}%`;
  }

}
customElements.define("video-autoplay", VideoAutoplay);

// Vertical: data-axis="vertical" (direction: up|down)
class ScrollSeamless extends HTMLElement {
  connectedCallback() {
    if (!document.getElementById("scroll-seamless-styles")) {
      const style = document.createElement("style");
      style.id = "scroll-seamless-styles";
      style.textContent = `
          @keyframes scroll-seamless-marquee-x {
            0% { transform: translateX(var(--scroll-seamless-start, 0)); }
            100% {
              transform: translateX(
                var(--scroll-seamless-end, calc(var(--scroll-seamless-direction, -1) * var(--scroll-seamless-distance, 50%)))
              );
            }
          }
          @keyframes scroll-seamless-marquee-y {
            0% { transform: translateY(var(--scroll-seamless-start, 0)); }
            100% {
              transform: translateY(
                var(--scroll-seamless-end, calc(var(--scroll-seamless-direction, -1) * var(--scroll-seamless-distance, 50%)))
              );
            }
          }
          .scroll-seamless__track {
            animation: scroll-seamless-marquee-x var(--scroll-seamless-duration, 30s) linear infinite;
            will-change: transform;
          }
          .scroll-seamless__track.is-vertical {
            animation-name: scroll-seamless-marquee-y;
          }
          .scroll-seamless__track.is-paused {
            animation-play-state: paused;
          }
          .scroll-seamless__mask {
            -webkit-mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
            mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
          }
          .scroll-seamless__mask.is-vertical {
            -webkit-mask-image: linear-gradient(to bottom, transparent, black 10%, black 90%, transparent);
            mask-image: linear-gradient(to bottom, transparent, black 10%, black 90%, transparent);
          }
          @media (prefers-reduced-motion: reduce) {
            .scroll-seamless__track {
              animation: none;
            }
          }
        `;
      document.head.appendChild(style);
    }

    const track = this.querySelector(".scroll-seamless__track");

    if (!track) {
      return;
    }

    const axisValue = (this.dataset.axis || this.dataset.orientation || "")
      .toLowerCase()
      .trim();
    const isVertical = axisValue === "vertical" || axisValue === "y";

    // Keep horizontal marquee behavior consistent regardless of page `dir`.
    // If a section/snippet needs RTL inside the marquee, set `dir` explicitly on <scroll-seamless>.
    if (!isVertical && !this.hasAttribute("dir")) {
      this.setAttribute("dir", "ltr");
    }
    const directionToken = (this.dataset.direction || "").toLowerCase().trim();
    const directionValue = isVertical
      ? directionToken === "down"
        ? 1
        : -1
      : directionToken === "right"
        ? 1
        : -1;
    track.style.setProperty("--scroll-seamless-direction", directionValue);
    if (directionValue === 1) {
      track.style.setProperty(
        "--scroll-seamless-start",
        "calc(-1 * var(--scroll-seamless-distance, 50%))",
      );
      track.style.setProperty("--scroll-seamless-end", "0px");
    } else {
      track.style.setProperty("--scroll-seamless-start", "0px");
      track.style.setProperty(
        "--scroll-seamless-end",
        "calc(var(--scroll-seamless-direction, -1) * var(--scroll-seamless-distance, 50%))",
      );
    }
    track.classList.toggle("is-vertical", isVertical);
    const mask = this.querySelector(".scroll-seamless__mask");
    if (mask) {
      mask.classList.toggle("is-vertical", isVertical);
    }

    const ensureClones = () => {
      // Measure original items width (excluding clones) without removing them first.
      const originals = Array.from(
        track.querySelectorAll(
          ':scope > :not([data-scroll-seamless-clone="true"])',
        ),
      );
      if (!originals.length) return;

      let baseLength = 0;
      originals.forEach((item) => {
        const rect = item.getBoundingClientRect();
        baseLength += isVertical ? rect.height : rect.width;
      });
      // Account for gap between original items.
      const trackStyle = getComputedStyle(track);
      const gap =
        parseFloat(isVertical ? trackStyle.rowGap : trackStyle.columnGap) || 0;
      if (originals.length > 1) baseLength += gap * (originals.length - 1);

      if (baseLength <= 0) return;

      const viewportLength = track.parentElement
        ? isVertical
          ? track.parentElement.offsetHeight
          : track.parentElement.offsetWidth
        : baseLength;
      const copies = Math.max(
        2,
        Math.ceil((viewportLength + baseLength) / baseLength),
      );

      // Skip rebuild if clone count and base length haven't changed significantly.
      const prevCopies = Number.parseInt(
        track.dataset.scrollSeamlessCopies || "0",
        10,
      );
      const prevBase = Number.parseFloat(
        track.dataset.scrollSeamlessBaseLength || "0",
      );
      if (prevCopies === copies && Math.abs(prevBase - baseLength) < 1) return;

      const existingClones = track.querySelectorAll(
        '[data-scroll-seamless-clone="true"]',
      );
      existingClones.forEach((clone) => clone.remove());

      const fragment = document.createDocumentFragment();
      for (let i = 1; i < copies; i += 1) {
        originals.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.dataset.scrollSeamlessClone = "true";
          clone.setAttribute("aria-hidden", "true");
          fragment.appendChild(clone);
        });
      }

      track.appendChild(fragment);

      // Eagerly load all lazy images in the track – seamless scrolling
      // makes every image visible regardless of viewport position.
      track
        .querySelectorAll("img.image-lazy-loading[data-srcset]")
        .forEach((img) => {
          img.srcset = img.dataset.srcset;
          img.removeAttribute("data-srcset");
          img.src = "";
          img.classList.remove("image-lazy-placeholder", "image-lazy-loading");
        });

      track.dataset.scrollSeamlessCopies = String(copies);
      track.dataset.scrollSeamlessBaseLength = String(baseLength);
    };

    const updateDuration = () => {
      ensureClones();

      const copies = Math.max(
        Number.parseInt(track.dataset.scrollSeamlessCopies || "2", 10),
        1,
      );
      const measuredLength = isVertical
        ? track.getBoundingClientRect().height || track.scrollHeight
        : track.getBoundingClientRect().width || track.scrollWidth;
      const storedLength = Number.parseFloat(
        track.dataset.scrollSeamlessBaseLength || "",
      );
      const baseDistance = Math.max(
        Number.isFinite(storedLength) && storedLength > 0
          ? storedLength
          : measuredLength / copies,
        1,
      );
      track.style.setProperty(
        "--scroll-seamless-distance",
        `${baseDistance}px`,
      );

      const speed = Number.parseFloat(
        this.dataset.speed ||
          track.dataset.speed ||
          track.dataset.marqueeSpeed ||
          "",
      );
      if (!Number.isFinite(speed) || speed <= 0) {
        return;
      }

      const duration = baseDistance / speed;
      track.style.setProperty("--scroll-seamless-duration", `${duration}s`);
    };

    updateDuration();
    this.updateDuration = updateDuration;
    this.ensureClones = ensureClones;
    window.addEventListener("resize", updateDuration);

    if (window.ResizeObserver) {
      let resizeRaf = null;
      const debouncedUpdate = () => {
        if (resizeRaf) return;
        resizeRaf = requestAnimationFrame(() => {
          resizeRaf = null;
          updateDuration();
        });
      };
      this.resizeObserver = new ResizeObserver(debouncedUpdate);
      this.resizeObserver.observe(track);
    }

    if (this.dataset.pauseOnHover === "true") {
      const pause = () => track.classList.add("is-paused");
      const resume = () => track.classList.remove("is-paused");

      track.addEventListener("mouseenter", pause);
      track.addEventListener("mouseleave", resume);
      track.addEventListener("focusin", pause);
      track.addEventListener("focusout", resume);
      track.addEventListener("touchstart", pause, { passive: true });
      track.addEventListener("touchend", resume);
    }
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.updateDuration) {
      window.removeEventListener("resize", this.updateDuration);
    }
  }
}
customElements.define("scroll-seamless", ScrollSeamless);

// Country Province Component
class CountryProvince extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.countrySelector = this.querySelector("[data-country-selector]");
    this.provinceSelector = this.querySelector("[data-province-selector]");
    if (!this.countrySelector || !this.provinceSelector) return;

    this.initCountry();
    this.initProvince();

    this.countrySelector.addEventListener(
      "change",
      this.onCountryChange.bind(this),
    );
  }

  initCountry() {
    const value = this.countrySelector.getAttribute("data-default");
    if (value != null) {
      this.selectByValue(this.countrySelector, value);
      this.onCountryChange();
    }
  }

  initProvince() {
    const value = this.provinceSelector.getAttribute("data-default");
    if (value != null) this.selectByValue(this.provinceSelector, value);
  }

  onCountryChange() {
    const opt =
      this.countrySelector.options[this.countrySelector.selectedIndex];
    const raw = opt.getAttribute("data-provinces");
    if (!raw) return;

    const provinces = JSON.parse(raw);
    this.clearOptions(this.provinceSelector);

    if (Array.isArray(provinces) && provinces.length > 0) {
      this.setOptions(this.provinceSelector, provinces);
      this.showProvince();
    } else {
      this.hideProvince();
    }
  }

  showProvince() {
    const wrapper = this.getProvinceWrapper();
    if (wrapper) wrapper.classList.remove("hidden");
  }

  hideProvince() {
    const wrapper = this.getProvinceWrapper();
    if (wrapper) wrapper.classList.add("hidden");
  }

  getProvinceWrapper() {
    return (
      this.provinceSelector.closest("[data-province-wrapper]") ||
      this.provinceSelector.closest(".cart-drawer-toolbar__select-wrap") ||
      this.provinceSelector.closest(".cart-page__shipping-select-wrap")
    );
  }

  selectByValue(selector, value) {
    for (let i = 0, count = selector.options.length; i < count; i++) {
      const option = selector.options[i];
      if (value === option.value || value === option.innerHTML) {
        selector.selectedIndex = i;
        break;
      }
    }
  }

  setOptions(selector, values) {
    if (values && Array.isArray(values) && values.length > 0) {
      const fragment = document.createDocumentFragment();
      values.forEach((value) => {
        const opt = document.createElement("option");
        opt.value = value[0];
        opt.innerHTML = value[1];
        fragment.appendChild(opt);
      });

      selector.appendChild(fragment);
    }
  }

  clearOptions(selector) {
    selector.innerHTML = "";
  }
}
customElements.define("country-province", CountryProvince);

// ── Feature Components ───────────────────────────────────────────

// Predictive Search Component
// Handles live search suggestions as the user types
class PredictiveSearch extends HTMLElement {
  static instances = new Set();

  constructor() {
    super();
    this.searchTerm = "";
    this.abortController = null;
    this.cachedResults = {};
    this.debounceTimer = null;
    this.resultsWrapper = null;
    this.emptyWrapper = null;
    this.input = null;
  }

  connectedCallback() {
    this.cacheElements();
    this.bindEvents();
    PredictiveSearch.instances.add(this);
  }

  disconnectedCallback() {
    this.unbindEvents();
    PredictiveSearch.instances.delete(this);
  }

  // Cache element references
  cacheElements() {
    this.resultsWrapper = this.querySelector("[data-search-results]");
    this.emptyWrapper = this.querySelector("[data-search-empty]");
    this.input = this.querySelector("[data-search-input]");
    this.clearButton = this.querySelector("[data-search-clear]");
    this.predictiveEnabled = this.dataset.predictiveEnabled === "true";
  }

  // Bind events
  bindEvents() {
    if (!this.input) return;
    this.onInput = this.handleInput.bind(this); // Bind input handler
    this.input.addEventListener("input", this.onInput);
    this.onTrendingClick = this.handleTrendingClick.bind(this);
    this.addEventListener("click", this.onTrendingClick);
    if (this.clearButton) {
      this.onClearClick = this.handleClearClick.bind(this);
      this.clearButton.addEventListener("click", this.onClearClick);
    }
  }

  // Unbind events
  unbindEvents() {
    if (this.input && this.onInput) {
      this.input.removeEventListener("input", this.onInput);
    }
    if (this.onTrendingClick) {
      this.removeEventListener("click", this.onTrendingClick);
    }
    if (this.clearButton && this.onClearClick) {
      this.clearButton.removeEventListener("click", this.onClearClick);
    }
  }

  // Handle trending search click
  handleTrendingClick(event) {
    const button = event.target.closest("[data-search-trending]");
    if (!button) return;
    const term = button.dataset.searchTrending || "";
    if (!term) return;
    if (this.input) {
      this.input.value = term;
      this.input.dispatchEvent(new Event("input", { bubbles: true }));
      this.input.focus();
    }
  }

  // Handle input event
  handleInput(event) {
    const value = event.target.value.trim();
    this.toggleClearButton(Boolean(value));
    if (!this.predictiveEnabled) return; // Skip if predictive search is disabled
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.searchTerm = value;
      if (!this.searchTerm) {
        this.clearResults();
        return;
      }
      this.getSearchResults();
    }, 200);
  }

  // Clear search results
  clearResults() {
    if (this.resultsWrapper) {
      this.resultsWrapper.classList.add("hidden");
      this.resultsWrapper.innerHTML = "";
    }
    if (this.emptyWrapper) {
      this.emptyWrapper.classList.remove("hidden");
    }
    this.toggleClearButton(Boolean(this.input && this.input.value.trim()));
    this.removeAttribute("loading");
    this.removeAttribute("results");
  }

  toggleClearButton(show) {
    if (!this.clearButton) return;
    this.clearButton.classList.toggle("opacity-0", !show);
    this.clearButton.classList.toggle("pointer-events-none", !show);
    this.clearButton.tabIndex = show ? 0 : -1;
  }

  handleClearClick() {
    if (!this.input) return;
    this.input.value = "";
    this.input.dispatchEvent(new Event("input", { bubbles: true }));
    this.toggleClearButton(false);
    this.clearResults();
    this.input.focus();
  }

  setLiveRegionLoading() {
    this.setAttribute("loading", "");
  }

  // Render search results
  renderSearchResults(markup) {
    if (this.resultsWrapper) {
      this.resultsWrapper.innerHTML = markup;
      this.resultsWrapper.classList.remove("hidden");
      if (window.webvista?.initLazyImageAnimation) {
        window.webvista.initLazyImageAnimation(this.resultsWrapper);
      }
    }
    if (this.emptyWrapper) {
      this.emptyWrapper.classList.add("hidden");
    }
    this.removeAttribute("loading");
    this.setAttribute("results", "");
  }

  // Fetch search results
  getSearchResults() {
    if (!this.predictiveEnabled) {
      this.clearResults();
      return;
    }
    const predictiveUrl =
      this.dataset.predictiveUrl || window.routes?.predictive_search_url;
    if (!predictiveUrl) {
      this.clearResults();
      return;
    }

    const queryKey = this.searchTerm.replace(/\s+/g, "-").toLowerCase();
    this.setLiveRegionLoading();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    const url = new URL(predictiveUrl, window.location.origin);

    url.search = new URLSearchParams({
      q: this.searchTerm,
      "resources[limit]": 10,
      "resources[limit_scope]": "each",
      "resources[options][fields]":
        "title,product_type,variants.title,vendor,variants.sku",
      section_id: this.dataset.sectionId || "predictive-search",
    });

    fetch(url.toString(), {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector(".shopify-section").innerHTML;

        PredictiveSearch.instances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === "AbortError") {
          return;
        }
        this.clearResults();
      });
  }
}
customElements.define("predictive-search", PredictiveSearch);

// Quick View Modal Component
// Lightweight modal shell — product interactions handled by product.js components
class QuickViewModal extends HTMLElement {
  constructor() {
    super();
    this.content = null;
    this.overlay = null;
    this.isOpen = false;
    this.opener = null;
    this.productUrl = "";
    this.fetchAbort = null;
    this.isLoading = false;
    this.loadingTrigger = null;
    this.hideTimer = null;
    this._focusTrap = null;
    this.onDocumentClick = this.onDocumentClick.bind(this);
  }

  connectedCallback() {
    this.cacheElements();
    document.addEventListener("click", this.onDocumentClick);
    if (Shopify.designMode) {
      this.onEditorSelect = this.onEditorSelect.bind(this);
      this.onEditorDeselect = this.onEditorDeselect.bind(this);
      document.addEventListener("shopify:section:select", this.onEditorSelect);
      document.addEventListener("shopify:section:deselect", this.onEditorDeselect);
    }
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.onDocumentClick);
    if (Shopify.designMode) {
      document.removeEventListener("shopify:section:select", this.onEditorSelect);
      document.removeEventListener("shopify:section:deselect", this.onEditorDeselect);
    }
    if (this.fetchAbort) {
      this.fetchAbort.abort();
      this.fetchAbort = null;
    }
  }

  cacheElements() {
    this.content = this.querySelector("[data-quick-view-content]");
    this.overlay = this.querySelector("[data-quick-view-overlay]");
  }

  onDocumentClick(event) {
    const trigger = event.target.closest("[data-quick-view-trigger]");
    if (trigger) {
      event.preventDefault();
      this.open(trigger);
      return;
    }
    const close = event.target.closest("[data-quick-view-close]");
    if (close && this.contains(close)) {
      event.preventDefault();
      this.hide();
    }
  }

  async open(trigger) {
    const handle = trigger.dataset.productHandle;
    if (!handle) return;
    if (this.isLoading && this.loadingTrigger === trigger) return;
    if (this.loadingTrigger && this.loadingTrigger !== trigger) {
      this.setTriggerLoading(this.loadingTrigger, false);
    }
    const returnFocusId = trigger.dataset.returnFocusId;
    const returnFocusTarget = returnFocusId
      ? document.getElementById(returnFocusId)
      : null;
    this.opener = returnFocusTarget || trigger;
    this.productUrl = trigger.dataset.productUrl || "";
    this.loadingTrigger = trigger;
    this.setTriggerLoading(trigger, true);
    const loaded = await this.loadProduct(handle);
    this.setTriggerLoading(trigger, false);
    this.loadingTrigger = null;
    if (loaded) {
      this.addToHistory(trigger);
      this.show();
    }
  }

  addToHistory(trigger) {
    const productId = trigger.dataset.productId;
    if (!productId) return;
    window.webvista?.addProductToHistory(productId);
  }

  show() {
    if (this.isOpen) return;
    this.isOpen = true;
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    this.classList.remove("hidden");
    this.classList.add("flex");
    this.setAttribute("aria-hidden", "false");
    this._savedScrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${this._savedScrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.documentElement.classList.add("overflow-hidden");
    if (this.overlay) {
      this.overlay.classList.add("opacity-100");
      this.overlay.classList.remove("opacity-0");
    }
    if (!this._focusTrap) {
      this._focusTrap = webvista.createFocusTrap(this, {
        onEscape: this.hide.bind(this),
      });
    }
    this.addEventListener("keydown", this._focusTrap);
    webvista.focusFirstElement(this);
  }

  hide() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("overflow-hidden");
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    if (typeof this._savedScrollY === "number") {
      window.scrollTo(0, this._savedScrollY);
      this._savedScrollY = null;
    }
    if (this.overlay) {
      this.overlay.classList.remove("opacity-100");
      this.overlay.classList.add("opacity-0");
    }
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    this.classList.add("hidden");
    this.classList.remove("flex");
    if (this._focusTrap) {
      this.removeEventListener("keydown", this._focusTrap);
    }
    if (this.opener && typeof this.opener.focus === "function") {
      this.opener.focus();
    }
  }

  async loadProduct(handle) {
    this.isLoading = true;
    this.setLoadingState(true);
    if (this.fetchAbort) this.fetchAbort.abort();
    const controller = new AbortController();
    this.fetchAbort = controller;
    try {
      const url = this.buildQuickViewUrl(handle);
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) {
        throw new Error("Quick view request failed.");
      }
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, "text/html");
      const content = parsed.querySelector("[data-quick-view-content]");
      if (!content) {
        throw new Error("Quick view content missing.");
      }
      const nextContent = content.cloneNode(true);
      if (this.content) {
        this.content.replaceWith(nextContent);
      } else {
        this.appendChild(nextContent);
      }
      this.content = nextContent;
      this.content.querySelectorAll("script").forEach((old) => {
        const fresh = document.createElement("script");
        for (const attr of old.attributes) fresh.setAttribute(attr.name, attr.value);
        if (old.textContent) fresh.textContent = old.textContent;
        old.replaceWith(fresh);
      });
      this.configureVariantPickerUrl();
      if (window.webvista?.initLazyImageAnimation) {
        window.webvista.initLazyImageAnimation(this.content);
      }
      return true;
    } catch (error) {
      if (error.name === "AbortError") return;
      this.setErrorState();
      return false;
    } finally {
      this.setLoadingState(false);
      this.isLoading = false;
    }
  }

  buildQuickViewUrl(handle) {
    const base = this.productUrl || `/products/${handle}`;
    const url = new URL(base, window.location.origin);
    url.searchParams.set("view", "quick-view-partial");
    return url.toString();
  }

  configureVariantPickerUrl() {
    const variantSelects = this.querySelector("high-variant-selects");
    if (!variantSelects || !this.productUrl) return;
    const url = new URL(this.productUrl, window.location.origin);
    url.searchParams.set("view", "quick-view-partial");
    variantSelects.dataset.url = url.toString();
    variantSelects.dataset.updateUrl = "false";
  }

  setLoadingState(isLoading) {
    if (!isLoading) return;
    if (this.content) {
      this.content.innerHTML =
        '<div class="w-full p-10 text-center text-sm text-gray-400">Loading...</div>';
    }
  }

  setTriggerLoading(trigger, isLoading) {
    if (!trigger) return;
    trigger.dataset.quickViewLoading = isLoading ? "true" : "false";
    trigger.setAttribute("aria-busy", isLoading ? "true" : "false");
    trigger.disabled = isLoading;
    trigger.classList.toggle("opacity-60", isLoading);
    trigger.classList.toggle("cursor-wait", isLoading);
    const label = trigger.querySelector("[data-quick-view-label]");
    const icon = trigger.querySelector("[data-quick-view-icon]");
    const spinner = trigger.querySelector("[data-quick-view-spinner]");
    if (label) {
      label.classList.toggle("hidden", isLoading);
    }
    if (spinner) {
      spinner.classList.toggle("hidden", !isLoading);
    }
    if (icon) {
      icon.classList.toggle("hidden", isLoading);
    }
  }

  openEditorPreview() {
    const handle = this.dataset.editorPreviewHandle;
    if (!handle) return;
    const fakeOpener = document.createElement("button");
    fakeOpener.dataset.productHandle = handle;
    fakeOpener.dataset.productUrl = this.dataset.editorPreviewUrl || "";
    this.open(fakeOpener);
  }

  onEditorSelect(event) {
    if (!event.detail?.sectionId) return;
    const sectionEl = document.getElementById(`shopify-section-${event.detail.sectionId}`);
    if (!sectionEl) return;
    if (!sectionEl.contains(this) && !this.closest(`#shopify-section-${event.detail.sectionId}`)) return;
    this.openEditorPreview();
  }

  onEditorDeselect(event) {
    if (!event.detail?.sectionId) return;
    const sectionEl = document.getElementById(`shopify-section-${event.detail.sectionId}`);
    if (!sectionEl) return;
    if (!sectionEl.contains(this) && !this.closest(`#shopify-section-${event.detail.sectionId}`)) return;
    if (this.isOpen) this.hide();
  }

  setErrorState() {
    if (this.content) {
      this.content.innerHTML =
        '<div class="w-full p-10 text-center text-sm text-gray-400">Unable to load. Please try again.</div>';
    }
  }
}
customElements.define("quick-view-modal", QuickViewModal);

// Product Card Component
class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.image = null;
    this.swatches = null;
    this.onMouseOver = this.onMouseOver.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
  }

  connectedCallback() {
    const imageHost = this.querySelector("[data-product-card-image]");
    this.image =
      imageHost?.tagName === "IMG"
        ? imageHost
        : imageHost?.querySelector("img");
    this.swatches = this.querySelector("[data-product-card-swatches]");
    if (!this.image || !this.swatches) return;
    if (this.swatches.dataset.previewInit === "true") return;
    this.swatches.dataset.previewInit = "true";

    this.swatches.addEventListener("mouseover", this.onMouseOver);
    this.swatches.addEventListener("focusin", this.onFocusIn);
    this.swatches.addEventListener("mouseleave", this.onMouseLeave);
    this.swatches.addEventListener("focusout", this.onFocusOut);
  }

  disconnectedCallback() {
    if (!this.swatches) return;
    this.swatches.removeEventListener("mouseover", this.onMouseOver);
    this.swatches.removeEventListener("focusin", this.onFocusIn);
    this.swatches.removeEventListener("mouseleave", this.onMouseLeave);
    this.swatches.removeEventListener("focusout", this.onFocusOut);
  }

  storeOriginal() {
    if (!this.image) return;
    if (!this.image.dataset.originalSrc) {
      this.image.dataset.originalSrc = this.image.getAttribute("src") || "";
    }
    if (!this.image.dataset.originalSrcset) {
      this.image.dataset.originalSrcset =
        this.image.getAttribute("srcset") || "";
    }
    if (!this.image.dataset.originalDataSrcset) {
      this.image.dataset.originalDataSrcset =
        this.image.getAttribute("data-srcset") || "";
    }
  }

  applyVariant(url) {
    if (!this.image || !url) return;
    this.storeOriginal();
    this.image.setAttribute("src", url);
    this.image.removeAttribute("data-srcset");
    this.image.removeAttribute("srcset");
  }

  restoreOriginal() {
    if (!this.image || !this.image.dataset.originalSrc) return;
    this.image.setAttribute("src", this.image.dataset.originalSrc);
    if (this.image.dataset.originalSrcset) {
      this.image.setAttribute("srcset", this.image.dataset.originalSrcset);
    }
    if (this.image.dataset.originalDataSrcset) {
      this.image.setAttribute(
        "data-srcset",
        this.image.dataset.originalDataSrcset,
      );
    }
  }

  setActiveSwatch(active) {
    if (!this.swatches) return;
    const items = Array.from(
      this.swatches.querySelectorAll("[data-product-card-swatch]"),
    );
    items.forEach((item) => {
      const isActive = item === active;
      item.classList.toggle("ring-2", isActive);
      item.classList.toggle("ring-offset-2", isActive);
      item.classList.toggle(
        "ring-[color:rgba(var(--color-foreground),0.4)]",
        isActive,
      );
      item.classList.toggle(
        "ring-offset-[color:rgb(var(--color-background))]",
        isActive,
      );
    });
  }

  onMouseOver(event) {
    const swatch = event.target.closest("[data-product-card-swatch]");
    if (!swatch || !this.swatches?.contains(swatch)) return;
    const url = swatch.dataset.variantImage || "";
    this.setActiveSwatch(swatch);
    this.applyVariant(url);
  }

  onFocusIn(event) {
    const swatch = event.target.closest("[data-product-card-swatch]");
    if (!swatch || !this.swatches?.contains(swatch)) return;
    const url = swatch.dataset.variantImage || "";
    this.setActiveSwatch(swatch);
    this.applyVariant(url);
  }

  onMouseLeave() {
    this.setActiveSwatch(null);
    this.restoreOriginal();
  }

  onFocusOut(event) {
    if (this.swatches?.contains(event.relatedTarget)) return;
    this.setActiveSwatch(null);
    this.restoreOriginal();
  }
}
customElements.define("product-card", ProductCard);

// Price Countdown Component
class PriceCountdown extends HTMLElement {
  constructor() {
    super();
    this.hoursNode = null;
    this.minutesNode = null;
    this.secondsNode = null;
    this.trigger = null;
    this.tooltip = null;
    this.endTime = null;
    this.timer = null;
    this.isTooltipOpen = false;
    this.isTouch = false;
    this.isHover = false;
    this.hoverCloseTimer = null;
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.update = this.update.bind(this);
    this.positionTooltip = this.positionTooltip.bind(this);
    this.onTooltipTransitionEnd = this.onTooltipTransitionEnd.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
  }

  connectedCallback() {
    this.hoursNode = this.querySelector("[data-countdown-hours]");
    this.minutesNode = this.querySelector("[data-countdown-minutes]");
    this.secondsNode = this.querySelector("[data-countdown-seconds]");
    this.trigger = this.querySelector("[data-countdown-trigger]");
    this.tooltip = this.querySelector("[data-countdown-tooltip]");
    const end = this.dataset.countdownEnd;
    if (!end || !this.hoursNode || !this.minutesNode || !this.secondsNode) {
      return;
    }

    const parsed = new Date(end);
    if (Number.isNaN(parsed.getTime())) {
      return;
    }
    this.endTime = parsed.getTime();

    this.isTouch = window.matchMedia("(hover: none)").matches;
    this.isHover = window.matchMedia("(hover: hover)").matches;
    if (this.isTouch && this.trigger && this.tooltip) {
      this.trigger.addEventListener("click", this.onTriggerClick);
      document.addEventListener("click", this.onDocumentClick);
      this.tooltip.addEventListener(
        "transitionend",
        this.onTooltipTransitionEnd,
      );
    }
    if (this.isHover && this.tooltip) {
      this.addEventListener("mouseenter", this.onMouseEnter);
      this.addEventListener("mouseleave", this.onMouseLeave);
      this.tooltip.addEventListener(
        "transitionend",
        this.onTooltipTransitionEnd,
      );
    }

    this.update();
    this.timer = window.setInterval(this.update, 1000);
  }

  disconnectedCallback() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
    if (this.isTouch && this.trigger) {
      this.trigger.removeEventListener("click", this.onTriggerClick);
      document.removeEventListener("click", this.onDocumentClick);
      if (this.tooltip) {
        this.tooltip.removeEventListener(
          "transitionend",
          this.onTooltipTransitionEnd,
        );
      }
    }
    if (this.isHover) {
      this.removeEventListener("mouseenter", this.onMouseEnter);
      this.removeEventListener("mouseleave", this.onMouseLeave);
      if (this.tooltip) {
        this.tooltip.removeEventListener(
          "transitionend",
          this.onTooltipTransitionEnd,
        );
      }
    }
    if (this.hoverCloseTimer) {
      window.clearTimeout(this.hoverCloseTimer);
      this.hoverCloseTimer = null;
    }
  }

  onTriggerClick(event) {
    event.stopPropagation();
    this.setTooltipOpen(!this.isTooltipOpen);
  }

  onDocumentClick(event) {
    if (!this.isTooltipOpen) return;
    if (this.contains(event.target)) return;
    this.setTooltipOpen(false);
  }

  onMouseEnter() {
    if (this.hoverCloseTimer) {
      window.clearTimeout(this.hoverCloseTimer);
      this.hoverCloseTimer = null;
    }
    this.setTooltipOpen(true);
  }

  onMouseLeave() {
    if (this.hoverCloseTimer) {
      window.clearTimeout(this.hoverCloseTimer);
    }
    this.hoverCloseTimer = window.setTimeout(() => {
      this.setTooltipOpen(false);
      this.hoverCloseTimer = null;
    }, 300);
  }

  setTooltipOpen(isOpen) {
    if (!this.tooltip) return;
    this.isTooltipOpen = isOpen;
    if (isOpen) {
      this.tooltip.classList.remove("hidden");
    }
    this.tooltip.classList.toggle("opacity-100", isOpen);
    this.tooltip.classList.toggle("delay-0", isOpen);
    this.tooltip.classList.toggle("opacity-0", !isOpen);
    this.tooltip.classList.toggle("delay-300", !isOpen);
    if (isOpen) {
      this.positionTooltip();
    }
  }

  positionTooltip() {
    if (!this.tooltip) return;
    const padding = 12;
    const viewportWidth = document.documentElement.clientWidth;
    const hostRect = this.getBoundingClientRect();
    const tooltipRect = this.tooltip.getBoundingClientRect();
    const tooltipHalf = tooltipRect.width / 2;
    let minCenter = padding + tooltipHalf;
    let maxCenter = viewportWidth - padding - tooltipHalf;
    if (minCenter > maxCenter) {
      minCenter = viewportWidth / 2;
      maxCenter = viewportWidth / 2;
    }
    const idealCenter = hostRect.left + hostRect.width / 2;
    const clampedCenter = Math.min(Math.max(idealCenter, minCenter), maxCenter);
    const leftWithinHost = clampedCenter - hostRect.left;
    this.tooltip.style.left = `${leftWithinHost}px`;
    this.tooltip.style.transform = "translateX(-50%)";
    this.tooltip.style.maxWidth = `${Math.max(0, viewportWidth - padding * 2)}px`;
  }

  onTooltipTransitionEnd(event) {
    if (!this.tooltip) return;
    if (event.propertyName !== "opacity") return;
    if (this.isTooltipOpen) return;
    this.tooltip.classList.add("hidden");
    this.tooltip.style.left = "";
    this.tooltip.style.transform = "";
    this.tooltip.style.maxWidth = "";
  }

  update() {
    if (!this.endTime) return;
    const now = Date.now();
    const diff = Math.max(0, this.endTime - now);
    const totalSeconds = Math.floor(diff / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.hoursNode.textContent = String(hours).padStart(2, "0");
    this.minutesNode.textContent = String(minutes).padStart(2, "0");
    this.secondsNode.textContent = String(seconds).padStart(2, "0");
  }
}
customElements.define("price-countdown", PriceCountdown);

// Typing Placeholder Component
// Animates input placeholder text with a typing/deleting loop through multiple phrases
// Focus: stops animation, clears placeholder, shows native caret
// Blur: resumes animation if input is empty
class TypingPlaceholder extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector("[data-typing-input]");
    if (!this.input) return;

    this.phrases = (this.dataset.phrases || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (this.phrases.length < 2) return;

    this.typeSpeed = parseInt(this.dataset.typeSpeed) || 80;
    this.deleteSpeed = parseInt(this.dataset.deleteSpeed) || 40;
    this.pauseDuration = parseInt(this.dataset.pause) || 1500;
    this.originalPlaceholder = this.input.getAttribute("placeholder") || "";
    this.currentIndex = 0;
    this.charIndex = 0;
    this.timer = null;
    this.isAnimating = false;

    this._onFocus = () => this.stop("");
    this._onBlur = () => { if (!this.input.value) this.start(); };
    this.input.addEventListener("focus", this._onFocus);
    this.input.addEventListener("blur", this._onBlur);

    if (document.activeElement !== this.input && !this.input.value) {
      this.start();
    }
  }

  disconnectedCallback() {
    this.stop();
    if (this.input) {
      this.input.removeEventListener("focus", this._onFocus);
      this.input.removeEventListener("blur", this._onBlur);
    }
  }

  start() {
    if (this.isAnimating) return;
    this.isAnimating = true;
    this.currentIndex = 0;
    this.charIndex = 0;
    this.input.setAttribute("placeholder", "");
    this.typePhrase();
  }

  stop(placeholder) {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.isAnimating = false;
    if (this.input) {
      this.input.setAttribute("placeholder", placeholder !== undefined ? placeholder : this.originalPlaceholder);
    }
  }

  typePhrase() {
    if (!this.isAnimating) return;
    const phrase = this.phrases[this.currentIndex];
    if (this.charIndex <= phrase.length) {
      this.input.setAttribute("placeholder", phrase.slice(0, this.charIndex));
      this.charIndex++;
      this.timer = setTimeout(() => this.typePhrase(), this.typeSpeed);
    } else {
      this.timer = setTimeout(() => this.deletePhrase(), this.pauseDuration);
    }
  }

  deletePhrase() {
    if (!this.isAnimating) return;
    const phrase = this.phrases[this.currentIndex];
    if (this.charIndex > 0) {
      this.charIndex--;
      this.input.setAttribute("placeholder", phrase.slice(0, this.charIndex));
      this.timer = setTimeout(() => this.deletePhrase(), this.deleteSpeed);
    } else {
      this.currentIndex = (this.currentIndex + 1) % this.phrases.length;
      this.timer = setTimeout(() => this.typePhrase(), this.typeSpeed);
    }
  }
}
customElements.define("typing-placeholder", TypingPlaceholder);
